import {
  IS_BROWSER,
  getLocaleCodeFromUrl,
  setLocalStorage,
  getImageFromProperty,
  getLiveEventStatus,
  checkForSkusArrayInfo,
  convertToBoolean,
  getUTTInfo,
  encodeSHA1,
  checkMysiteOrPersonalOffer,
  toAmount,
  toQuantity,
  getFrequencyLabel,
  getItemVariant,
  getItemCouponCode,
  DEFAULT_AFFILIATION,
  DEFAULT_BRAND,
  resolveItemBrand,
  isRedesignPOUrl,
  enableNewFeature,
  getTrackingPrefix,
} from 'src/utils'
import TagManager from 'react-gtm-module'
import { APPConfig, getLocalStorage, getSessionStorage } from 'config/appConfig'
import { pageNames } from 'src/routes/pathParams'
import { isToShowOverrideSubscription } from 'src/utils/multipleSubscriptionUtils'
import {
  PERSONAL_OFFER,
  STOREFRONT,
  NUSKIN_DISPLAY_NAME,
  NOT_AVAILABLE,
} from './constant'

const ITEM_LIST_NAME = Object.freeze({
  BUNDLE: 'bundle',
  INDIVIDUAL: 'individual',
})
const TRACKING_EVENTS = Object.freeze({
  ADD_SHIPPING_INFO: 'add_shipping_info',
  ADD_PAYMENT_INFO: 'add_payment_info',
})
const CHECKOUT_EVENT_TYPES = Object.freeze({
  SHIPPING: 'shipping',
  PAYMENT: 'payment',
})
const PERSONAL_OFFER_TRACKING_FALLBACK = Object.freeze({})
const BUY_NOW_TRACKING_FALLBACK = Object.freeze({})

function emitSalesAIPixelEvent(eventType = '', properties = {}) {
  try {
    if (
      window.ExperiencePixel &&
      typeof window.ExperiencePixel.track === 'function' &&
      eventType
    ) {
      window.ExperiencePixel.track(eventType, properties)
    }
  } catch (e) {
    console.error('Error during ExperiencePixel tracking', e)
  }
}

function trackSalesAIPixelLogin(options = {}) {
  try {
    const {
      accountId = '',
      personId = '',
      method = 'password',
      status = 'success',
    } = options || {}
    const payload = {
      accountId: accountId || '',
      personId: personId || '',
      method,
      status,
      page_type: 'login',
    }

    emitSalesAIPixelEvent('login', payload)
    return payload
  } catch (error) {
    console.error('Error in trackSalesAIPixelLogin', error)
    return {}
  }
}

function trackSalesAIPixelBeginCheckout(options = {}) {
  try {
    const {
      cartResponse = {},
      totalValue,
      cartId = '',
      currencyCode = '',
      accountId = '',
      personId = '',
      step = 'shipping',
    } = options || {}

    const derivedCartData = cartResponse || {}

    const sourceItems = Array.isArray(derivedCartData?.items)
      ? derivedCartData.items
      : []

    const pixelItems = sourceItems.map(item => {
      const firstSku = item?.skus?.[0] || {}
      const quantity = Number(item?.quantity) || 0
      const unitPrice = toAmount(
        firstSku?.price ||
          item?.totalValue?.priceAfterDiscount ||
          item?.unitValue?.priceAfterDiscount ||
          0
      )

      return {
        productId:
          firstSku?.skuId || firstSku?.identifier || item?.itemId || '',
        sku: firstSku?.skuId || firstSku?.identifier || item?.itemId || '',
        name:
          firstSku?.skuProperties?.name ||
          firstSku?.properties?.name ||
          item?.properties?.name ||
          '',
        price: unitPrice,
        quantity,
        lineTotal: toAmount(unitPrice * quantity),
      }
    })

    const totals = derivedCartData?.value || {}
    const resolvedCartTotalValue =
      totalValue ??
      totals?.grandTotal ??
      totals?.priceAfterDiscount ??
      totals?.totalAmountPaid ??
      0

    const payload = {
      accountId: accountId || '',
      personId: personId || '',
      cartId: cartId || derivedCartData?.cartId || derivedCartData?.id || '',
      orderId: null,
      items: pixelItems,
      currency: currencyCode || derivedCartData?.currencyCode || getCurrency(),
      cartTotalValue: toAmount(resolvedCartTotalValue),
      step,
    }

    emitSalesAIPixelEvent('begin_checkout', payload)
    return payload
  } catch (error) {
    console.error('Error in trackSalesAIPixelBeginCheckout', error)
    return {}
  }
}

function getBuyNowTrackingData(options = {}) {
  try {
    if (!IS_BROWSER) {
      return BUY_NOW_TRACKING_FALLBACK
    }

    const isBuyNowFlow =
      convertToBoolean(options?.isExpressCheckout) ||
      convertToBoolean(getLocalStorage('isExpressCart'))

    return isBuyNowFlow ? { buy_now: 'yes' } : BUY_NOW_TRACKING_FALLBACK
  } catch (error) {
    return BUY_NOW_TRACKING_FALLBACK
  }
}

/**
 * Gets personal offer / mysite tracking data for GTM events
 * Returns sponsor_id, shop_name, and location on personal offer AND mysite (storefront) pages
 * pitch_id only applies on personal offer pages ('NA' on mysite); subdomain only applies on mysite pages ('NA' on personal offer)
 * offer_name is only included on personal offer pages, populated from personalOfferData and 'NA' when the offer has no name
 * Otherwise returns empty object
 * @returns {Object} Object containing sponsor_id, shop_name, pitch_id, subdomain, and location (plus offer_name on personal offer pages)
 */
function getPersonalOfferTrackingData({ includeBuyNow = true } = {}) {
  try {
    if (!IS_BROWSER) {
      return PERSONAL_OFFER_TRACKING_FALLBACK
    }

    const contextValue = checkMysiteOrPersonalOffer()
    if (contextValue !== PERSONAL_OFFER && contextValue !== STOREFRONT) {
      return PERSONAL_OFFER_TRACKING_FALLBACK
    }

    const isPersonalOffer = contextValue === PERSONAL_OFFER
    const storefrontData = getSessionStorage(STOREFRONT) || {}

    const {
      getPoSponsorId,
      getSubdomainName,
    } = require('src/utils/mySiteUtils')

    const personalOfferData = isPersonalOffer
      ? getSessionStorage('personalOffer') || {}
      : {}

    const sponsorId = isPersonalOffer
      ? personalOfferData?.sponsorId || getPoSponsorId()
      : storefrontData?.user?.sapId ||
        storefrontData?.user?.userId ||
        getSubdomainName()

    const pitchId =
      (isPersonalOffer && personalOfferData?.offerId) ||
      getSessionStorage('pitch_id') ||
      NOT_AVAILABLE

    const shopName = storefrontData?.user?.shopName || NUSKIN_DISPLAY_NAME

    const offerName =
      (isPersonalOffer && personalOfferData?.name) || NOT_AVAILABLE

    const location = contextValue
    const subdomain = isPersonalOffer
      ? NOT_AVAILABLE
      : getSubdomainName() || NOT_AVAILABLE

    return {
      sponsor_id: sponsorId,
      shop_name: shopName,
      pitch_id: pitchId,
      ...(isPersonalOffer && { offer_name: offerName }),
      subdomain: subdomain,
      location: location,
      ...(includeBuyNow && getBuyNowTrackingData()),
    }
  } catch (error) {
    console.log('Error getting personal offer tracking data:', error)
    return PERSONAL_OFFER_TRACKING_FALLBACK
  }
}

function getPersonalOfferName() {
  if (checkMysiteOrPersonalOffer() !== PERSONAL_OFFER) {
    return ''
  }
  return getSessionStorage('personalOffer')?.name || ''
}

/**
 * Gets personal offer sponsor ID as affiliation if on personal offer page
 * Otherwise returns empty string
 * @returns {string} Sponsor ID if on personal offer page, otherwise empty string
 */
function getPersonalOfferAffiliationForTracking() {
  if (checkMysiteOrPersonalOffer() !== PERSONAL_OFFER) {
    return ''
  }
  const personalOfferData = getPersonalOfferTrackingData()
  return personalOfferData?.sponsor_id || ''
}

/**
 * Adds a user ID to local storage for tracking purposes.
 * Nuskin will use it for their tracking
 *
 * @param {Object} options - Options object
 * @param {string} options.userId - The user ID to add
 */
function addUserIdForTracking(options) {
  const { userId } = options || {}

  if (userId) {
    setLocalStorage('nuskin.account', { user: { id: userId } })
  }
}

/**
 * Sets data layer event with GTM
 *
 * @param {string} event - Name of the event
 * @param {string} name - Name of the cookie
 * @param {string} value - Value of the cookie
 */
function setDataLayer(event = 'TrackCookie', name, value) {
  try {
    TagManager.dataLayer({
      dataLayer: {
        event,
        name,
        value,
        platform: 'equinox',
      },
    })
  } catch (error) {
    console.log('GTM Error', error)
  }
}

/**
 * Gets the currency code based on the country code and locale.
 * Looks up the locale in the countryCode object to find the currency code for that locale.
 * Falls back to empty string if no currency code found.
 */
function getCurrency() {
  const storeData = APPConfig.getAppConfig().storeData || {}
  const locale = getLocaleCodeFromUrl() || ''
  const currencyCode = storeData[locale]?.currency || ''
  return currencyCode
}

/**
 * Tracks a cookie by sending its name and value to the data layer.
 *
 * @param {Object} options - Options object
 * @param {string} options.cookieName - Name of the cookie to track
 * @param {string} options.cookieValue - Value of the cookie to track
 */
function trackCookie(options) {
  const { cookieName, cookieValue } = options || {}

  if (cookieName && cookieValue) {
    setDataLayer(eventName, cookieName, cookieValue)
  }
}

/**
 * Gets the product data formatted for Google Tag Manager from the provided options.
 *
 * @param {Object} options - Options object containing product data
 * @returns {Object} - Product data formatted for Google Tag Manager
 */
function getGTMProducts(options) {
  const {
    price = '',
    variant = '',
    apiResponse,
    item_type = '',
    item_view = '',
  } = options || {}
  const product = apiResponse?.[0]?.skus?.[0] || options?.product?.[0] || {}
  const productProperties = product?.skuProperties || product?.properties
  const categoryName = options?.product?.[0]?.properties?.categoryId || ''

  return {
    name:
      productProperties?.name ||
      productProperties?.slug ||
      options?.item?.productName ||
      '',
    id:
      product?.skuId || product?.skus?.[0]?.skuId || options?.item?.skuId || '',
    price: price || product?.price || options?.item?.totalPrice || '',
    category:
      categoryName ||
      productProperties?.categoryId ||
      options?.item?.categoryId ||
      '',
    variant: variant || options?.item?.variantLabel || '',
    quantity:
      product?.quantity ||
      options?.product?.[0]?.quantity ||
      options?.item?.quantity ||
      '',
    item_type: item_type,
    item_view: item_view,
    brand: productProperties?.brand || options?.item?.band || '',
  }
}

/**
 * Tracks an enhanced ecommerce cart event with product data.
 *
 * @param {Object} options - Options object
 * @param {string} options.type - Event type
 * @param {string} [options.method=''] - Ecommerce method (e.g. 'add', 'remove')
 * @param {Object} options.products - Product data
 */
function trackEnhancedCart(options = {}) {
  const { type, method = '' } = options
  const products = getGTMProducts(options)
  const gtmData = {
    event: type,
    ecommerce: {
      currencyCode: getCurrency(),
      override_cart: isToShowOverrideSubscription() ? 'yes' : 'no',
      [method]: {
        products: [{ ...products }],
      },
    },
  }
  trackDataLayer(gtmData)
}

function trackMysiteAddCartEvent(options = {}) {
  const { type, method = '', product = {} } = options
  trackDataLayer({
    event: type,
    ecommerce: {
      currencyCode: getCurrency(),
      [method]: {
        products: [product],
      },
    },
  })
}

function trackMysiteViewAllEvent(options = {}) {
  const { type, method = '', product = [] } = options
  trackDataLayer({
    event: type,
    ecommerce: {
      currencyCode: getCurrency(),
      [method]: product,
    },
  })
}

function trackEnhancedCartEvent(options = {}) {
  const { type, method = '', price = '' } = options
  const products = getGTMProducts(options)
  const gtmData = {
    event: type,
    currency: getCurrency(),
    value: price,
    items: [{ ...products }],
  }
  trackDataLayer(gtmData)
}

/**
 * Tracks a Google Tag Manager product click event.
 *
 * @param {Object} options - Options object
 * @param {string} [options.type] - Event type
 * @param {string} [options.action] - Action field for GTM event
 * @param {Object} options.product - Product data
 * @param {boolean} [options.isSpecialProduct=false] - Whether product is a special product
 * @param {string} [options.categoryId] - Product category ID
 */
function trackProductClick(options = {}) {
  const {
    type,
    action,
    product,
    isSpecialProduct = false,
    categoryId,
  } = options

  const sku = findDefaultSku({ sku: product?.sku })

  const products = []
  products.push({
    name: sku?.properties?.name || '',
    id: sku?.identifier,
    price: isSpecialProduct
      ? product?.prices?.[0]?.price
      : sku?.price || product?.totalValue?.priceAfterDiscount,
    category: isSpecialProduct
      ? categoryId
      : product?.properties?.productCategory,
  })

  const gtmData = {
    event: type || 'productClick',
    ecommerce: {
      click: {
        actionField: action,
        products,
      },
    },
  }
  trackDataLayer(gtmData)
}

function trackProductClickEvent(options = {}) {
  const {
    type,
    product,
    isSpecialProduct = false,
    categoryId,
    promotion,
    promotionClass = '',
    offerId = '',
  } = options
  const sku = findDefaultSku({ sku: product?.sku })

  const productsOfEvent = []
  productsOfEvent.push({
    item_id: sku?.identifier,
    item_name: sku?.properties?.name || '',
    price: isSpecialProduct
      ? product?.prices?.[0]?.price
      : sku?.price || product?.totalValue?.priceAfterDiscount,
    item_category: isSpecialProduct
      ? categoryId
      : product?.properties?.productCategory,
    item_brand: resolveItemBrand({
      skuProps: sku?.properties,
      productProps: product?.properties,
    }),
    index: 0,
    discount: product?.totalValue?.totaldiscount || '',
    quantity: '',
  })
  let gtmData = {}
  if (promotion) {
    gtmData = {
      event: type || '',
      creative_name: '',
      creative_slot: promotionClass,
      promotion_id: offerId,
      promotion_name: '',
      items: productsOfEvent,
    }
  } else {
    gtmData = {
      event: type || '',
      item_list_id: sku?.identifier,
      item_list_name: sku?.properties?.name || '',
      items: productsOfEvent,
    }
  }
  trackDataLayer(gtmData)
}

/**
 * Tracks a Google Tag Manager promotion impression event.
 *
 * @param {Object} options - Options object
 * @param {Object} [options.promotionDetails={}] - Promotion details
 */
function trackPromotionImpression(options = {}) {
  const { promotionDetails = {} } = options
  const gtmData = {
    event: 'promotionImpression',
    ecommerce: {
      promoView: {
        promotions: promotionDetails,
      },
    },
  }
  trackDataLayer(gtmData)
}

/**
 * Transforms the provided products array into a checkout products array.
 *
 * Maps over the products array and constructs a checkout product object for each product.
 * The shape of the object depends on the isFromCustomEvent flag.
 *
 * If true, maps to the enhanced ecommerce checkout object shape.
 * If false, maps to the standard ecommerce checkout object shape.
 *
 * @param {Object[]} products - The products array to transform
 * @param {boolean} isFromCustomEvent - Whether the data is from a custom event
 * @returns {Object[]} The transformed checkout products array
 */
function getCheckoutProduct(
  products = [],
  isFromCustomEvent = false,
  isFromUTTEvent = false
) {
  return products?.map((product, idx) => {
    let item = {}
    const name = product?.skus?.[0]?.skuProperties?.name ?? ''
    const id = product?.skus?.[0]?.skuId ?? ''
    const brand = product?.skus?.[0]?.skuProperties?.brand ?? ''
    const coupon = product?.properties?.giftOfferId || 'NA'
    const category = product?.skus?.[0]?.skuProperties?.productCategory ?? ''
    const price =
      product?.unitValue?.priceAfterDiscount ?? product?.skus?.[0]?.price ?? 0
    const quantity = product?.quantity ?? 0
    const variant = product?.skus?.[0]?.skuProperties?.variantLabel ?? ''
    const discount = product?.unitValue?.discount ?? ''
    const subTotal = price * quantity ?? 0
    if (isFromCustomEvent) {
      item = {
        item_id: id,
        item_name: name,
        coupon: coupon,
        discount,
        index: idx + 1,
        item_brand: brand,
        item_category: category,
        item_list_id: '',
        item_list_name: '',
        item_variant: '',
        location_id: '',
        price,
        quantity,
      }
    } else if (isFromUTTEvent) {
      item = {
        name: name,
        sku: id,
        subTotal: subTotal,
        category: category,
        quantity: quantity,
      }
    } else {
      item = {
        name,
        id,
        price,
        brand,
        category,
        variant,
        quantity,
      }
    }
    return item
  })
}

function trackExpressCheckoutSubmitOrder(props) {
  const { gtmData = {}, id = '', paymentType = '' } = props
  const actionfeild = {
    id: id,
    memberId: gtmData?.userId || '',
    orderType: 'Standard',
    affiliation: 'Online Store',
    revenue: gtmData?.properties?.oneTimeItemsTotal || 0,
    tax: gtmData?.value?.overAllTax || '',
    shipping: gtmData?.value?.cartDeliveryCharge || '',
    coupon: gtmData?.promotionDetails?.codes?.[0]?.code || '',
    action: 'purchase',
  }
  const payLoad = {
    event: 'express-purchase',
    paymentType: paymentType,
    ecommerce: {
      currencyCode: gtmData?.currencyCode || '',
      purchase: {
        actionField: { ...actionfeild },
        products: getCheckoutProduct(gtmData?.items),
      },
    },
  }
  trackGTMEvents(payLoad)
}

/**
 * Transforms the product data from the subscription drawer into an array of item details for tracking purposes.
 *
 * @param {Object} drawerData - The product data from the subscription drawer.
 * @returns {Object[]} The transformed array of item details.
 */
function getSubscriptionDrawerData(drawerData = {}) {
  const itemDetailsArr = []
  Object.values(drawerData)?.map((product, idx) => {
    let item = {}

    const {
      skuInfo = {},
      totalValue = {},
      subscription = {},
      orderProcessedDate = '',
      quantity = '',
      itemId = '',
      skuId = '',
    } = product || {}
    const name = product?.skus?.[0]?.skuProperties?.name || ''
    const discount = totalValue?.discount || 'NA'
    const total = totalValue?.grandTotal || ''
    const frequency =
      subscription?.frequencyPeriod + '-' + subscription?.frequencyType

    item = {
      item_id: itemId,
      item_name: name,
      discount,
      total,
      quantity,
      frequency,
      processing_day: orderProcessedDate.toString(),
    }

    itemDetailsArr.push(item)
  })
  return itemDetailsArr
}

/**
 * Transforms the checkout products data into an array of item details for tracking purposes.
 *
 * @param {Object} checkoutResponse - The checkout response data.
 * @param {Object[]} checkoutResponse.items - The array of checkout items.
 * @returns {Object[]} The transformed array of item details.
 */

function getCheckoutProductV2(checkoutResponse = {}) {
  const products = checkoutResponse?.items || []
  const itemDetailsArr = []
  products?.map((product, idx) => {
    let item = {}
    if (product?.isSubscription) {
      const transformedSkusInfo = checkForSkusArrayInfo(product?.skus)
      const { skuInfo = {} } = transformedSkusInfo?.[0] || {}
      const id = skuInfo?.skuId || ''
      const itemId = product?.itemId || ''
      const itemName = product?.skus?.[0]?.skuProperties?.name || ''
      const quantity = product?.quantity || ''
      const discount = product?.totalValue?.discount || 'NA'
      const total = product?.totalValue?.grandTotal || ''
      const frequency =
        product?.subscription?.frequencyPeriod +
        '-' +
        product?.subscription?.frequencyType

      item = {
        item_id: itemId,
        item_name: itemName,
        discount,
        total,
        quantity,
        frequency,
        processing_day: '',
      }
      itemDetailsArr.push(item)
    }
  })
  return itemDetailsArr
}

/**
 * Tracks a subscription order event in Google Tag Manager.
 *
 * @param {Object} subscriptionResponse - The subscription order response data.
 * @param {Object} subscriptionResponse.order - The subscription order details.
 * @param {Object[]} subscriptionResponse.selectedSubscriptions - The selected subscription details.
 * @param {string} subscriptionResponse.orderId - The subscription order ID.
 * @param {string} subscriptionResponse.userId - The user ID associated with the subscription order.
 */
function trackSubscriptionOrderNowGTMData(subscriptionResponse = {}) {
  const orderInfo = subscriptionResponse?.order?.value || {}
  const gtmData =
    subscriptionResponse?.selectedSubscriptions?.[0]?.subscriptions || []

  const productsArray = []
  gtmData.forEach(subscription => {
    const itemDetails = subscription?.itemInfo?.[0] || {}
    const transformedSkusInfo = checkForSkusArrayInfo(itemDetails?.skus)
    const { skuInfo = {} } = transformedSkusInfo?.[0] || {}
    const product = {
      name: skuInfo?.name || '',
      id: skuInfo?.id || '',
      price: itemDetails?.price || 0,
      brand: skuInfo?.skuProperties?.brand || '',
      category: skuInfo?.skuProperties?.productCategory || '',
      variant: skuInfo?.skuProperties?.variantLabel || '',
      quantity: itemDetails?.quantity || 0,
    }
    productsArray.push(product)
  })
  let actionField = {
    id: subscriptionResponse?.orderId || '',
    memberId: subscriptionResponse?.userId || '',
    orderType: 'Subscription',
    affiliation: 'Online Store',
    revenue: orderInfo?.grandTotal || 0,
    tax: orderInfo?.overAllTax || 0,
    shipping: orderInfo?.totalDeliveryCharge || 0,
    coupon: '',
    action: 'purchase',
  }

  trackGTMEvents({
    event: 'subscriptionOrder',
    ecommerce: {
      currencyCode: 'USD',
      purchase: {
        actionField: { ...actionField },
        products: productsArray,
      },
    },
  })

  trackGTMEvents({
    event: 'checkout-purchase',
    ecommerce: {
      currencyCode: 'USD',
      purchase: {
        actionField: {
          ...actionField,
        },
        products: productsArray,
      },
    },
  })
}

/**
 * Transforms the product data from the checkout response into an array of item details for the agreement section.
 *
 * @param {Object} products - The products data from the checkout response.
 * @param {boolean} isSubscription - Indicates whether the products are subscriptions.
 * @returns {Object[]} The transformed array of item details.
 */

function getProductsForAgreementSection(products = {}, isSubscription = false) {
  const itemDetailsArr = []
  Object.values(products).map((product, idx) => {
    let item = {}
    if (product?.isSubscription || isSubscription) {
      const transformedSkusInfo = checkForSkusArrayInfo(product?.skus)
      const { skuInfo = {} } = transformedSkusInfo?.[0] || {}
      const name = skuInfo?.skuProperties?.name || ''
      const id = skuInfo?.skuId || ''
      const price = product?.totalValue?.priceAfterDiscount || ''

      item = {
        name,
        id,
        price,
      }
      itemDetailsArr.push(item)
    }
  })
  return itemDetailsArr
}

/**
 * Tracks an enhanced ecommerce checkout event in Google Tag Manager.
 *
 * @param {Object} options - Options object
 * @param {string} options.type - The type of checkout event (checkout, checkout_option)
 * @param {string} options.action - The checkout action (add, remove, purchase, etc)
 * @param {Object[]} options.products - Array of product data objects
 *
 * The function constructs the GTM data layer object with the mapped
 * event, action, currency, and transformed checkout products array.
 */
function trackEnhancedCheckout(options = {}) {
  const { type, action, products } = options
  const checkoutProducts = getCheckoutProduct(products)
  const gtmData = {
    event: type,
    ecommerce: {
      override_cart: isToShowOverrideSubscription() ? 'yes' : 'no',
      checkout: {
        actionField: action,
        products: checkoutProducts,
      },
    },
  }
  trackDataLayer(gtmData)
}

function trackEnhancedCheckoutEvent(options = {}) {
  const { type, action, products, value = '' } = options

  let gtmData = {}
  const checkoutProducts = products && getCheckoutProduct(products, true)
  if (type === 'store_credits') {
    gtmData = {
      event: type,
      currency: getCurrency(),
      override_cart: isToShowOverrideSubscription() ? 'yes' : 'no',
      amount: value,
    }
  } else {
    gtmData = {
      event: type,
      currency: getCurrency(),
      override_cart: isToShowOverrideSubscription() ? 'yes' : 'no',
      value: value,
      items: checkoutProducts,
    }
  }
  trackDataLayer(gtmData)
}

/**
 * Gets the selected shipping method from the cart response.
 *
 * @param {Object} options - Options object
 * @param {Object} options.cartResponse - Cart response object
 * @returns {Object} The selected shipping method object
 */
function getShippingMethod(options = {}) {
  const { cartResponse } = options
  const selectedDeliveryMethod = cartResponse?.deliveryDetails?.methods?.find(
    deliveryMethod => {
      return deliveryMethod?.isSelected == true
    }
  )
  return selectedDeliveryMethod
}

/**
 * Tracks a custom checkout event in Google Tag Manager.
 *
 * @param {Object} options - Options object
 * @param {Object} options.cartResponse - The cart response object
 * @param {string} options.eventType - The type of event to track (shipping or payment)
 *
 * The function checks the eventType and maps it to the appropriate GTM event name.
 * It then constructs the GTM data layer object with the mapped event, currency,
 * and checkout products.
 *
 */
function trackCustomCheckoutEvent(options = {}) {
  const { cartResponse, eventType } = options
  const eventTypes = {
    [CHECKOUT_EVENT_TYPES.SHIPPING]: TRACKING_EVENTS.ADD_SHIPPING_INFO,
    [CHECKOUT_EVENT_TYPES.PAYMENT]: TRACKING_EVENTS.ADD_PAYMENT_INFO,
  }

  const event = eventTypes[eventType]

  if (!event || !cartResponse) {
    return
  }
  if (event) {
    const gtmData = {
      event,
      currency: cartResponse?.currencyCode || '',
      value: cartResponse?.value?.grandTotal || '',
      override_cart: isToShowOverrideSubscription() ? 'yes' : 'no',
      items: getCheckoutProduct(cartResponse?.items || [], true),
    }

    if (eventType === CHECKOUT_EVENT_TYPES.SHIPPING) {
      const selectedDeliveryMethod = getShippingMethod({ cartResponse })
      const shippingAddress = cartResponse?.deliveryDetails?.address || {}
      gtmData['shipping_tier'] = selectedDeliveryMethod?.id || ''
      gtmData['shipping_country'] = shippingAddress?.country || ''
      gtmData['shipping_region'] =
        shippingAddress?.state || shippingAddress?.region || ''
    } else if (eventType == 'payment') {
      // Added 1st payment since GTM supports only one
      gtmData['payment_tier'] = cartResponse?.payments?.[0]?.type || ''
    }
    trackDataLayer(gtmData)
  }
}

function trackAddShippingInfoEvent(options = {}) {
  const {
    cartData = {},
    shippingMethod,
    currencyCode,
    eventValue,
    discountValue,
    shippingValue,
    offer,
    itemAffiliation = '',
    distributorId = '',
  } = options

  const totals = cartData?.value || {}
  const resolvedCurrency =
    currencyCode || cartData?.currencyCode || getCurrency()
  const resolvedValue = toAmount(
    eventValue ??
      totals?.grandTotal ??
      totals?.priceAfterDiscount ??
      totals?.totalAmountPaid ??
      0
  )

  const discountAmt =
    discountValue ??
    totals?.totalDiscount ??
    totals?.cartDiscount ??
    totals?.overAllDiscount
  const resolvedDiscount = discountAmt ? toAmount(discountAmt) : 'NA'
  const resolvedShipping = toAmount(
    shippingValue ??
      totals?.cartDeliveryChargeAfterDiscount ??
      totals?.cartDeliveryCharge ??
      0
  )
  const resolvedCoupon = resolveCartCoupon(cartData)
  const resolvedOfferCandidate =
    offer ?? resolvePromotionDisplayMessages(cartData, resolvedCoupon)
  const resolvedOffer =
    getPersonalOfferName() ||
    (resolvedOfferCandidate && `${resolvedOfferCandidate}`.trim()) ||
    'NA'
  const resolvedShippingMethod =
    shippingMethod || getShippingMethod({ cartResponse: cartData }) || {}
  const shippingTierLabel =
    getShippingTierLabel(resolvedShippingMethod) ||
    resolvedShippingMethod?.id ||
    resolvedShippingMethod?.name ||
    ''
  const defaultAffiliation = resolveDefaultAffiliation(
    cartData,
    itemAffiliation,
    distributorId
  )

  const items = mapCartItemsToPathToPurchase(cartData, {
    resolvedCoupon,
    defaultAffiliation,
  })

  const shippingAddress = cartData?.deliveryDetails?.address || {}
  const personalOfferData = getPersonalOfferTrackingData({
    includeBuyNow: false,
  })
  const buyNowTrackingData = getBuyNowTrackingData()

  const payload = {
    event: TRACKING_EVENTS.ADD_SHIPPING_INFO,
    ...personalOfferData,
    ...buyNowTrackingData,
    ecommerce: {
      currency: resolvedCurrency,
      value: resolvedValue,
      offer: resolvedOffer,
      discount: resolvedDiscount,
      shipping_tier: shippingTierLabel,
      override_cart: isToShowOverrideSubscription() ? 'yes' : 'no',
      shipping: resolvedShipping,
      shipping_country: shippingAddress?.country || '',
      shipping_region: shippingAddress?.state || shippingAddress?.region || '',
      items,
    },
  }

  trackDataLayer(payload)

  return payload
}

function trackAddPaymentInfoEvent(options = {}) {
  const {
    cartData = {},
    paymentType,
    currencyCode,
    eventValue,
    discountValue,
    offer,
    itemAffiliation = '',
    distributorId = '',
  } = options

  const totals = cartData?.value || {}
  const resolvedCurrency =
    currencyCode || cartData?.currencyCode || getCurrency()
  const resolvedValue = toAmount(
    eventValue ??
      totals?.grandTotal ??
      totals?.priceAfterDiscount ??
      totals?.totalAmountPaid ??
      0
  )
  const discountAmt =
    discountValue ??
    totals?.totalDiscount ??
    totals?.cartDiscount ??
    totals?.overAllDiscount
  const resolvedDiscount = discountAmt ? toAmount(discountAmt) : 'NA'
  const resolvedCoupon = resolveCartCoupon(cartData)
  const resolvedOfferCandidate =
    offer ?? resolvePromotionDisplayMessages(cartData, resolvedCoupon)
  const resolvedOffer =
    getPersonalOfferName() ||
    (resolvedOfferCandidate && `${resolvedOfferCandidate}`.trim()) ||
    'NA'
  const resolvedPaymentType = paymentType || cartData?.payments?.[0]?.type || ''
  const defaultAffiliation = resolveDefaultAffiliation(
    cartData,
    itemAffiliation,
    distributorId
  )

  const items = mapCartItemsToPathToPurchase(cartData, {
    resolvedCoupon,
    defaultAffiliation,
  })

  const shippingAddress = cartData?.deliveryDetails?.address || {}
  const personalOfferData = getPersonalOfferTrackingData()
  const buyNowTrackingData = getBuyNowTrackingData()

  const payload = {
    event: TRACKING_EVENTS.ADD_PAYMENT_INFO,
    ...personalOfferData,
    ...buyNowTrackingData,
    ecommerce: {
      currency: resolvedCurrency,
      value: resolvedValue,
      offer: resolvedOffer,
      discount: resolvedDiscount,
      override_cart: isToShowOverrideSubscription() ? 'yes' : 'no',
      shipping_country: shippingAddress?.country || '',
      shipping_region: shippingAddress?.state || shippingAddress?.region || '',
      payment_type: resolvedPaymentType,
      items,
    },
  }

  trackDataLayer(payload)

  return payload
}

/**
 * Tracks an enhanced ecommerce product impression event in Google Tag Manager.
 *
 * Constructs a GTM data layer object containing product impression data
 * for each product, including name, id, price, category, variant, list,
 * and position. The data layer event name is 'productImpression'.
 */
function trackEnhancedProductImpression(data = {}) {
  let productsData = []
  data?.product?.forEach((product, index) => {
    const sku = findDefaultSku({ sku: product?.sku })
    const productData = {
      name: sku?.properties?.name || product?.properties?.name || '',
      // in PLP call, for bundles and kits, we will not be receiving SKU data
      id: sku?.identifier || product?.identifier || '',
      // price: sku?.prices?.[0]?.price,
      price:
        sku?.totalValue?.priceAfterDiscount ||
        product?.totalValue?.priceAfterDiscount ||
        sku?.prices?.[0]?.price ||
        product?.prices?.[0]?.price ||
        '',
      // brand: 'Nuskin',
      category: data?.category?.properties?.name,
      variant: '',
      list: 'PLP',
      position:
        (data?.pageableInfo?.page - 1) * data?.pageableInfo?.size + index + 1,
    }
    productsData.push(productData)
  })
  const GTMLayer = {
    event: 'productImpression',
    ecommerce: {
      currencyCode: getCurrency(),
      impressions: productsData,
    },
  }
  trackDataLayer(GTMLayer)
}

function trackEnhancedProductViewEvent(data = {}) {
  const { type = '' } = data
  let productsData = []
  data?.product?.forEach((product, index) => {
    const sku = findDefaultSku({ sku: product?.sku })
    const productData = {
      item_name: sku?.properties?.name || product?.properties?.name || '',
      // in PLP call, for bundles and kits, we will not be receiving SKU data
      item_id: sku?.identifier || product?.identifier || '',
      item_brand: resolveItemBrand({
        skuProps: sku?.properties,
        productProps: product?.properties,
      }),
      price:
        sku?.totalValue?.priceAfterDiscount ||
        product?.totalValue?.priceAfterDiscount ||
        sku?.prices?.[0]?.price ||
        product?.prices?.[0]?.price ||
        '',
      discount: product?.totalValue?.totaldiscount || '',
      category:
        data?.category?.properties?.name ||
        product?.properties?.productCategory ||
        '',
    }
    productsData.push(productData)
  })
  const GTMLayer = {
    event: type || 'view_item_list',
    ecommerce: {
      items: productsData,
    },
  }
  trackDataLayer(GTMLayer)
}
/**
 * Tracks an enhanced ecommerce product detail impression event in Google Tag Manager.
 *
 * Constructs a GTM data layer object containing product detail impression data
 * for the given product, including name, id, price, brand, category.
 * The data layer event name is 'productDetailImpression'.
 */
function trackEnhancedProductDetailImpression(data = {}) {
  const product = data?.product?.[0]
  const replacementProductClick = data?.replacementProductClick || false
  const isQuickview = data?.isQuickview || false
  const products = getTrackingProducts({
    product,
    isToTrackBundleItems: false,
    replacementProductClick,
    isQuickview,
  })

  const GTMLayer = {
    event: 'productDetailImpression',
    ecommerce: {
      detail: {
        actionField: { list: '', action: 'detail' },
        products,
      },
    },
  }
  trackDataLayer(GTMLayer)
}

function trackV2SubscriptionButtonClick(data) {
  trackDataLayer(data)
}
/**
 * Finds the default SKU from a list of SKUs.
 *
 * @param {Object} options - The options object
 * @param {Object[]} options.sku - The list of SKUs to search
 * @returns {Object} The default SKU object
 */
function findDefaultSku(options) {
  const { sku } = options || {}
  return sku?.find(sku => sku?.default == true)
}

/**
 * Gets the SKUs to use for tracking/reporting for a product.
 * For bundles, gets the default SKU for each bundled product if isToTrackBundleItems is true.
 * Otherwise returns the top-level bundle product.
 * For non-bundles, returns the default SKU if one exists.
 *
 * @param {Object} options - The options object
 * @param {Object} options.product - The product
 * @param {Boolean} options.isToTrackBundleItems [isToTrackBundleItems=true] - whether to track bundleItems default value is true
 * @returns {Object[]} The list of tracking products
 */
function getSkus(options) {
  const { product, isToTrackBundleItems = true } = options || {}

  const skus = []
  if (product?.skuKit || product?.type?.toLowerCase() == 'bundle') {
    if (isToTrackBundleItems) {
      product?.skuKit.forEach(skuKit => {
        const defaultSku = findDefaultSku({ sku: skuKit?.sku })
        if (defaultSku) {
          skus.push(defaultSku)
        }
      })
    } else {
      skus.push(product)
    }
  } else {
    const defaultSku = findDefaultSku({ sku: product?.sku })
    if (defaultSku) {
      skus.push(defaultSku)
    }
  }
  return skus
}

/**
 * Gets the product data to use for tracking/reporting.
 * Maps the SKUs to a simplified tracking format.
 *
 * @param {Object} options - The options object
 * @param {Object} options.product - The product
 * @returns {Object[]} The list of tracking products
 */
function getTrackingProducts(options) {
  const { product, replacementProductClick, isQuickview } = options || {}
  const categoryName = product?.category?.[0]?.properties?.name || ''
  const skus = getSkus(options)
  return skus.map(sku => {
    const isproductStatus =
      sku?.properties?.productStatus?.toLowerCase() == 'discontinued'
    const replacementSkuId = sku?.properties?.discontinuedSkuReplacement || ''
    const relavantReplacementProduct =
      product?.replacementProduct?.find(item => {
        if (item?.sku) {
          return item?.sku?.find(sku => sku?.identifier === replacementSkuId)
        }
        return null
      }) || {}
    return {
      name: sku?.properties?.name || '',
      id: sku?.identifier || '',
      price: Number(
        sku?.totalValue?.priceAfterDiscount || sku?.price || ''
      ).toFixed(2),
      brand: 'Nuskin',
      category: categoryName,
      item_type: replacementProductClick
        ? 'replacement_product'
        : isproductStatus
          ? sku?.properties?.productStatus?.toLowerCase()
          : '',
      item_view: isQuickview ? 'PLP quickview' : 'PDP',
      replacement_product:
        isproductStatus && Object.keys(relavantReplacementProduct).length > 0
          ? 'available'
          : 'not available',
    }
  })
}

/**
 * Tracks search result events.
 *
 * If there are search results, sends a search event with the provided options.
 * Uses the searchType to determine event details.
 *
 * If no results, calls trackNoSearchResult() instead.
 *
 * @param {Object} options - The tracking options
 * @param {string} options.searchType - The type of search
 * @param {Object[]} options.products - The matching products
 * @param {boolean} options.isPlainSearch - True if a regular search
 */
function trackSearchResult(options = {}) {
  const { searchType, products, isPlainSearch } = options

  if (products || searchType == 'fullSearch') {
    if (isPlainSearch) {
      const gtmData = {
        searchType,
      }
      trackSearchEvent({ ...options, gtmData })
    }
  } else {
    trackNoSearchResult(options)
  }
}

function trackSearchResultEvent(options = {}) {
  const { searchType, products, isPlainSearch, suggestionList = {} } = options
  if (products || searchType == 'fullSearch') {
    if (isPlainSearch) {
      let selected = {}
      if (products.length == 1) {
        let trackingItem = products[0]
        selected = {
          item_id: trackingItem?.identifier,
          item_name: trackingItem?.properties?.name || '',
          price: trackingItem?.prices?.[0]?.price || '',
          pv: trackingItem?.priceFacets?.PV || '',
          cv: trackingItem?.priceFacets?.CV || '',
        }
      }

      const gtmData = {
        type: searchType,
        suggesstions: suggestionList,
        selected,
      }
      trackSearchEvent({ ...options, gtmData, isEvent: true })
    }
  } else {
    trackNoSearchResultEvent(options)
  }
}

/**
 * Tracks a no search result event.
 *
 * When no search results are found, sends a searchNoResults event
 * with the provided options.
 *
 * @param {Object} options - The tracking options
 * @param {string} options.searchTerm - The search term used
 */
function trackNoSearchResult(options = {}) {
  const { searchTerm } = options
  const gtmData = {
    event: 'searchNoResults',
    searchType: 'searchNoResults',
    typedQuery: searchTerm,
    query: searchTerm,
  }

  trackDataLayer(gtmData)
}

function trackNoSearchResultEvent(options = {}) {
  const { searchTerm } = options
  const gtmData = {
    event: 'search',
    type: 'no_result',
    typedQuery: searchTerm,
  }
  trackDataLayer(gtmData)
}
/**
 * Tracks a recent search event.
 *
 * When a user searches using a recent search term,
 * sends a searchRequested event with the provided options.
 *
 * @param {Object} options - The tracking options
 * @param {string} options.searchTerm - The recent search term used
 */
function trackRecentSearch(options = {}) {
  const { searchTerm } = options

  const gtmData = {
    searchType: 'recentSearch',
  }
  trackSearchEvent({ ...options, gtmData })
}

function trackRecentSearchEvent(options = {}) {
  const { searchTerm, recentSearchList } = options

  const gtmData = {
    type: 'recent_search',
    selected: {},
    suggestions: recentSearchList,
  }

  const enableLiveEvent = getLiveEventStatus()
  const { isEvent = true } = options || {}
  if (enableLiveEvent === 'true' && isEvent) {
    gtmData.event = 'search'
  } else {
    gtmData.event = 'searchRequested'
  }
  trackDataLayer(gtmData)
}
/**
 * Tracks a predictive search event.
 *
 * When a user searches and gets predictive search results,
 * sends a search event with predictiveSearch searchType
 * and additional product data in gtmData.
 *
 * @param {Object} options - The tracking options
 * @param {string} options.typedQuery - The text typed by the user
 * @param {string} options.searchTerm - The actual search term used
 * @param {Object} options.product - The product result object
 */
function trackPredictiveSearch(options) {
  const { typedQuery, searchTerm, product } = options || {}
  const gtmData = {
    searchType: 'predictiveSearch',
    item: {},
  }

  let trackingItem = product

  if (product?.sku) {
    trackingItem = product?.sku?.find(sku => sku?.default == true)
  }

  if (product) {
    const imageUrl = getImageFromProperty(trackingItem?.properties) || ''
    gtmData.item = {
      img: imageUrl,
      itemName: trackingItem?.properties?.name || '',
      sku: trackingItem?.identifier,
      price: trackingItem?.prices?.[0]?.price || '',
      pv: trackingItem?.priceFacets?.PV || '',
      cv: trackingItem?.priceFacets?.CV || '',
      isExclusive: trackingItem?.properties?.isExclusive || false,
      title: trackingItem?.properties?.name,
      fullImage: imageUrl,
    }
  }

  trackSearchEvent({ ...options, gtmData })
}

function trackPredictiveSearchEvent(options) {
  const { typedQuery, searchTerm, product, suggestionProductList } =
    options || {}
  const gtmData = {
    type: 'predictive_search',
    selected: {},
    suggestions: [],
  }
  let trackingItem = product

  if (product?.sku) {
    trackingItem = product?.sku?.find(sku => sku?.default == true)
  }

  if (product) {
    const imageUrl = getImageFromProperty(trackingItem?.properties) || ''
    gtmData.selected = {
      item_id: trackingItem?.identifier,
      item_name: trackingItem?.properties?.name || '',
      price: trackingItem?.prices?.[0]?.price || '',
      pv: trackingItem?.priceFacets?.PV || '',
      cv: trackingItem?.priceFacets?.CV || '',
    }
    gtmData.suggestions = suggestionProductList
  }

  trackSearchEvent({ ...options, gtmData, isEvent: true })
}

/**
 * Tracks a search suggestion event.
 *
 * When a user gets search suggestions, sends a search event
 * with suggestion searchType and search result data in gtmData.
 *
 * @param {Object} options - The tracking options
 * @param {string} options.searchTerm - The search term used
 * @param {Object[]} options.searchResult - The suggestion search results
 */
function trackSearchSuggestions(options) {
  const { searchTerm, searchResult } = options || {}
  const gtmData = {
    searchType: 'suggestion',
    searchMode: 'custom',
    result: searchResult,
  }
  trackSearchEvent({ ...options, gtmData })
}

/**
 * Tracks a search event.
 *
 * Sends a search event to the data layer
 * with search query data from the options parameter.
 *
 * @param {Object} options - The tracking options
 */
function trackSearchEvent(options) {
  const enableLiveEvent = getLiveEventStatus()
  const {
    typedQuery,
    searchTerm,
    searchResult,
    gtmData = {},
    isEvent = false,
  } = options || {}

  gtmData.query = searchTerm || ''
  gtmData.typedQuery = typedQuery || searchTerm || ''
  if (enableLiveEvent === 'true' && isEvent) {
    gtmData.event = 'search'
  } else {
    gtmData.event = 'searchRequested'
  }
  trackDataLayer(gtmData)
}

/**
 * Tracks a product add to cart event.
 *
 * Sends add to cart event data to the data layer
 * when a product is added to the cart on the PDP.
 *
 * @param {Object[]} options - The product data
 */
function trackPDPAddToCart(options) {
  const locale = getLocaleCodeFromUrl()
  let userId = ''
  try {
    userId = JSON.parse(localStorage.getItem('nuskin.account') || '{}')?.user
      ?.id
  } catch (e) {}

  const gtmData = {
    event: options?.event || 'nuskin-shop-add-to-cart',
    sku: options?.[0]?.skus?.[0]?.skuId,
    //  to do for bundle, existing issue
    qty: options?.[0]?.quantity,
    maxQty: 99,
    cntryCd: locale.split('_')[1],
    language: locale.split('_')[0],
    ...(!options?.isExpressCheckout && {
      userId,
      isAdr: false,
      isPitchCart: false,
    }),
    referrer: options?.referrer || 'productPage',
    domain: IS_BROWSER ? document.domain : '',
    country: locale.split('_')[1],
    ...getBuyNowTrackingData(options),
  }
  if (options?.price) {
    gtmData['price'] = options?.price
  }
  if (options?.isExpressCheckout) {
    gtmData['item_name'] = options?.[0]?.properties?.slug || ''
    gtmData['item_brand'] = 'Nuskin'
    gtmData['item_id'] = options?.[0]?.itemId || ''
    gtmData['link_text'] = 'buy now'
    gtmData['link_url'] = window?.location?.pathname
    gtmData['session_id'] = getLocalStorage('sessionId') || ''
  }
  trackDataLayer(gtmData)
}

/**
 * Tracks a quick view add to cart event.
 *
 * Sends add to cart event data to the data layer
 * when a product is added to the cart from the quick view modal.
 *
 * @param {Object[]} options - The product data
 */
function trackQuickViewAddToCart(options) {
  const locale = getLocaleCodeFromUrl()
  let userId = ''
  try {
    userId = JSON.parse(localStorage.getItem('nuskin.account') || '{}')?.user
      ?.id
  } catch (e) {}

  const gtmData = {
    event: 'nuskin-shop-add-to-cart',
    sku: options?.[0]?.skus?.[0]?.skuId,
    product: options?.[0]?.skus?.[0],
    qty: options?.[0]?.quantity,
    userId,
    cntryCd: locale.split('_')[1],
    adr: false,
    referrer: 'productCard',
    domain: IS_BROWSER ? document.domain : '',
  }
  trackDataLayer(gtmData)
}

/**
 * Tracks adding a product to a subscription.
 *
 * Sends add to cart event data to the data layer when a
 * product is added to a subscription.
 *
 * @param {Object} options - The options object
 * @param {Object} options.product - The product object
 * @param {string} options.product.skuId - The product SKU id
 * @param {number} options.quantity - The quantity added
 */
function trackAddToSubscription(options = {}) {
  const gtmData = {
    event: 'nuskin-shop-add-to-cart',
    sku: options?.product?.skuId,
    qty: options?.quantity,
    adr: true,
    product: options?.product,
    referrer: 'productCard',
  }
  trackDataLayer(gtmData)
}

/**
 * @description
 * This function is used to track the user events in the My Profile section.
 * It sends the event to Google Tag Manager with the event type, category, and event trigger.
 * The function takes an object as an argument with the following properties:
 * @param { String } - The event type.
 * @param { String } - The category of the event.
 * @param { String } - The name of the event.
 * @param { Object } - The preferences of the event.
 */
function trackMyProfileEvents(options = {}) {
  const {
    type = '',
    categoryName = '',
    eventName = '',
    preferences = {},
  } = options
  const gtmData = {
    event: type,
    category: categoryName,
    eventType: eventName,
    eventTrigger: preferences,
  }
  trackDataLayer(gtmData)
}

/**
 * Sends event data to the Google Tag Manager data layer.
 *
 * @param {Object} options - Event data to send to the data layer.
 */
function trackGTMEvents(option = {}) {
  trackDataLayer(option)
}

/**
 * Tracks a Contact Me event for interested products.
 *
 * Sends a Contact_me event to the data layer when a user fills in their
 * contact details for an interested product and clicks "Contact Me".
 *
 * @param {Object} options - Event data to track
 * @param {string} [options.contactPrefer=''] - The preferred contact method (e.g., 'sms/whats app')
 * @param {string} [options.learnMore=''] - Additional learning preference information
 */
function trackContactMe(options = {}) {
  const { contactPrefer = '', learnMore = '' } = options

  const gtmData = {
    event: 'Contact_me',
    Contact_prefer: contactPrefer,
    Learn_more: learnMore,
  }

  trackDataLayer(gtmData)
}

/**
 * Tracks pincode validation error events to GTM.
 *
 * Sends a Pincode_error event to the data layer when an error message
 * appears for invalid pincode validation.
 *
 * @param {Object} options - Event data to track
 * @param {string} [options.pincodeMsg=''] - The error message displayed for invalid pincode
 * @param {number} [options.uniqueEventId] - Unique identifier for the event
 */
function trackPincodeError(options = {}) {
  const { pincodeMsg = '' } = options

  const gtmData = {
    event: 'Pincode_error',
    Pincode_msg: pincodeMsg,
  }

  trackDataLayer(gtmData)
}

/**
 * Tracks GSTIN validation error events to GTM.
 *
 * Sends a GSTIN_error event to the data layer when an error message
 * appears for invalid GSTIN number validation.
 *
 * @param {Object} options - Event data to track
 * @param {string} [options.gstinMsg=''] - The error message displayed for invalid GSTIN
 * @param {number} [options.uniqueEventId] - Unique identifier for the event
 */
function trackGSTINError(options = {}) {
  const { gstinMsg = '' } = options

  const gtmData = {
    event: 'GSTIN_error',
    gstin_msg: gstinMsg,
  }
  trackDataLayer(gtmData)
}

function trackBuyItAgainEvent(options = {}) {
  const { type = '', items = [], order_id = '' } = options
  const gtmData = {
    event: type,
    currency: getCurrency(),
    transaction_id: order_id,
    items: items,
  }
  trackDataLayer(gtmData)
}

/**
 * Tracks an account upgrade event.
 *
 * @param {Object} options - Event data to include:
 * @param {string} options.event - The event name to track
 * @param {Object} options - Additional event data
 */
function trackAccountUpgrade(options = {}) {
  const gtmData = {
    event: 'nuskin-account-conversion',
    ...options,
  }
  trackDataLayer(gtmData)
}

/**
 * Tracking filter selection
 * @date 2023/9/1 - 17:59:09
 *
 * @param { Object } - options
 * @param { String } - options.filterKey - Filter identifier
 * @param { Object } - options.filterValue - Selected filter object
 * @param { Object } - options.faceFilter - All the available filter object
 */
function trackFilter(options = {}) {
  const { filterKey, filterValue, facetFilter } = options

  // On selection it will be still on false. The value will set true after invoking the API
  if (filterValue.selected == false) {
    const selectedFilter = facetFilter.filter(filter => filter.key == filterKey)
    const type = selectedFilter?.[0]?.name
    if (type) {
      const gtmData = {
        event: 'filterSearch',
        type,
        value: filterValue?.name,
      }
      trackDataLayer(gtmData)
    }
  }
}

/**
 * @description - Adds items to the Data Layer array within the global scope. Note: Assumes presence of a variable named ‘window’ that contains a reference to the Global Window Object. Additionally assumes existence of a member property on said window object labeled ‘dataLayer’ that represents the Array instance used by the GTM container to capture and process Data Layer information.
 * @param {Object} options
 * @param {string} options.event - A unique name identifying the specific activity occurring on the site. Typically corresponds directly to one of the pre-defined triggers defined within the GTM UI.
 * @param {string} options.name - Arbitrary additional details pertaining to the triggered event
 * @param {string} options.value - Numeric quantities associated with the given event such as monetary amounts, number of items added to cart, etc. May also contain more structured data if desired but keep in mind potential size constraints placed on the event
 * @param {string} [options.platform='equinox'] - Identifies the particular platform through which the event occurred. Useful for filtering or reporting purposes later on once raw data has been collected. Defaults to the literal string ‘equinox’ indicating this remains
 */
/**
 * Sanitizes an object for GTM to remove circular references
 * Firefox has strict serialization that fails on circular refs
 * @param {*} value - Value to sanitize
 * @param {Set} seen - Set of objects already seen (for circular reference detection)
 * @returns {*} Sanitized value safe for serialization
 */
function sanitizeForGTM(value, seen = new Set()) {
  if (value === null || value === undefined || typeof value !== 'object') {
    return value
  }

  if (seen.has(value)) {
    return undefined
  }

  seen.add(value)

  if (Array.isArray(value)) {
    const result = value.map(item => sanitizeForGTM(item, seen))
    seen.delete(value)
    return result
  }

  const sanitized = {}
  try {
    for (const [key, val] of Object.entries(value)) {
      if (
        typeof val === 'function' ||
        typeof val === 'symbol' ||
        key.startsWith('_')
      ) {
        continue
      }
      sanitized[key] = sanitizeForGTM(val, seen)
    }
  } catch (error) {
    return undefined
  }

  seen.delete(value)
  return sanitized
}

function trackDataLayer(options = {}) {
  options.platform = 'equinox'
  if (IS_BROWSER) {
    const sanitized = sanitizeForGTM(options)
    TagManager.dataLayer({ dataLayer: sanitized })
  }
}

function resolveCartCoupon(cartData = {}, explicitCoupon = '') {
  if (explicitCoupon) {
    return explicitCoupon
  }

  const promotionDetails = cartData?.promotionDetails || {}
  const promotionCodes = promotionDetails?.codes
  if (Array.isArray(promotionCodes) && promotionCodes.length) {
    const primaryCode = promotionCodes[0]
    if (typeof primaryCode === 'string') {
      return primaryCode
    }
    return (
      primaryCode?.code ||
      primaryCode?.couponCode ||
      primaryCode?.promotionCode ||
      'NA'
    )
  }

  if (promotionDetails?.code) {
    return promotionDetails.code
  }

  const promotions = promotionDetails?.promotions
  if (Array.isArray(promotions)) {
    const promo = promotions.find(
      promotion =>
        promotion?.code || promotion?.couponCode || promotion?.promotionCode
    )
    return promo?.code || promo?.couponCode || promo?.promotionCode || ''
  }

  return ''
}

function resolvePromotionDisplayMessages(cartData = {}, fallback = 'NA') {
  const messages = new Set()

  const recordMessage = value => {
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed) {
        messages.add(trimmed)
      }
    }
  }

  const collectFromPromotionDetails = promotionDetails => {
    if (!promotionDetails) {
      return
    }

    if (Array.isArray(promotionDetails?.promotions)) {
      for (const promotion of promotionDetails.promotions) {
        recordMessage(promotion?.displayMessage)
        if (Array.isArray(promotion?.breakup)) {
          for (const entry of promotion.breakup) {
            recordMessage(entry?.displayMessage)
          }
        }
      }
    }
  }

  collectFromPromotionDetails(cartData?.promotionDetails)

  if (Array.isArray(cartData?.items)) {
    for (const item of cartData.items) {
      collectFromPromotionDetails(item?.promotionDetails)
    }
  }

  if (!messages.size) {
    return fallback
  }

  return Array.from(messages).join(', ')
}

function resolveDefaultAffiliation(
  cartData = {},
  itemAffiliation = '',
  distributorId = ''
) {
  const poAffiliation = getPersonalOfferAffiliationForTracking()
  if (poAffiliation) {
    return poAffiliation
  }

  const affiliationCandidate =
    itemAffiliation ||
    distributorId ||
    cartData?.properties?.brandAffiliateId ||
    cartData?.properties?.mysiteSponsorId ||
    cartData?.properties?.affiliateId ||
    cartData?.properties?.sponsorId ||
    ''

  if (!affiliationCandidate || affiliationCandidate === 'NSHOUSE') {
    return DEFAULT_AFFILIATION
  }

  return affiliationCandidate
}

function resolveCartItemSku(item = {}) {
  const skus = Array.isArray(item?.skus) ? item.skus : []
  if (!skus.length) {
    return {}
  }

  const defaultSku = skus.find(
    sku => sku?.default === true || sku?.isDefault === true
  )
  if (defaultSku) {
    return defaultSku
  }

  const nonBundleSku = skus.find(sku => {
    const type = sku?.type || sku?.subType
    return typeof type === 'string' && type.toLowerCase() !== 'bundle'
  })
  if (nonBundleSku) {
    return nonBundleSku
  }

  const skuWithBrand = skus.find(
    sku =>
      sku?.skuProperties?.brand ||
      sku?.properties?.brand ||
      sku?.skuProperties?.brandName ||
      sku?.skuProperties?.productBrand
  )
  if (skuWithBrand) {
    return skuWithBrand
  }

  return skus[0]
}

function resolvePathToPurchaseItemType(item = {}, sku = {}, skuProps = {}) {
  const typeCandidates = [
    sku?.type,
    sku?.subType,
    item?.type,
    skuProps?.type,
    item?.properties?.type,
    skuProps?.productStatus,
    item?.properties?.productStatus,
  ]

  const hasReplacementType = typeCandidates.some(candidate => {
    if (!candidate) {
      return false
    }
    return `${candidate}`.toLowerCase() === 'replacement'
  })

  const hasReplacementFlag =
    convertToBoolean(item?.isReplacementProduct) ||
    convertToBoolean(item?.properties?.isReplacementProduct) ||
    (Array.isArray(item?.replacementProduct) &&
      item.replacementProduct.length > 0)

  return hasReplacementType || hasReplacementFlag ? 'replacement' : 'standard'
}

function mapCartItemsToPathToPurchase(cartData = {}, options = {}) {
  const { resolvedCoupon = '', defaultAffiliation = DEFAULT_AFFILIATION } =
    options
  const cartItems = Array.isArray(cartData?.items) ? cartData.items : []

  const processedItemIds = new Set()
  const result = []

  const poAffiliation = getPersonalOfferAffiliationForTracking()

  cartItems.forEach((item, index) => {
    const sku = item?.skus?.[0] || {}
    const skuProps = sku?.skuProperties || sku?.properties || {}

    const itemId = sku?.skuId || sku?.identifier || item?.itemId || ''

    if (!itemId || processedItemIds.has(itemId)) {
      return
    }

    processedItemIds.add(itemId)

    const resolvedBrand = resolveItemBrand({
      skuProps,
    })

    const discountAmt = item?.unitValue?.discount ?? item?.totalValue?.discount
    const itemDiscount = discountAmt ? toAmount(discountAmt) : 'NA'
    const itemPrice = toAmount(
      item?.unitValue?.priceAfterDiscount ??
        item?.totalValue?.priceAfterDiscount ??
        sku?.price
    )
    const subscriptionFrequency = getFrequencyLabel(item?.subscription)
    const perItemCoupon = getItemCouponCode(item) || resolvedCoupon || ''
    const affiliationValue =
      item?.properties?.brandAffiliateId ||
      item?.properties?.affiliationId ||
      item?.properties?.affiliation ||
      defaultAffiliation
    const prevSessionCartSkuIdsArr = getSessionStorage('prevSessionCartSkuIds')
    const itemType = resolvePathToPurchaseItemType(item, sku, skuProps)

    const finalAffiliation =
      poAffiliation || affiliationValue || DEFAULT_AFFILIATION

    result.push({
      item_id: itemId,
      item_name:
        skuProps?.name ||
        item?.properties?.name ||
        item?.properties?.slug ||
        skuProps?.slug ||
        '',
      affiliation: finalAffiliation,
      coupon_code: perItemCoupon,
      discount: itemDiscount,
      index: result.length,
      item_brand: resolvedBrand,
      item_category:
        skuProps?.productCategory || item?.properties?.categoryId || '',
      item_list_name:
        item?.properties?.listName ||
        (Array.isArray(item?.skus) && item?.skus?.length > 1
          ? ITEM_LIST_NAME.BUNDLE
          : ITEM_LIST_NAME.INDIVIDUAL),
      item_variant: getItemVariant(sku),
      price: itemPrice,
      quantity: toQuantity(
        item?.quantity ?? sku?.quantity ?? sku?.skuQuantity ?? null
      ),
      item_subscription: Boolean(item?.isSubscription),
      item_subscription_override: isToShowOverrideSubscription() ? 'yes' : 'no',
      item_subscription_frequency: subscriptionFrequency,
      item_persistent: prevSessionCartSkuIdsArr?.includes(sku?.skuId) || false,
      item_type: itemType,
    })
  })

  return result
}

function resolveSelectItemSku(productCandidate = {}, baseProduct = {}) {
  if (productCandidate && Object.keys(productCandidate).length) {
    return productCandidate
  }

  let baseSkus = []

  if (Array.isArray(baseProduct?.skus)) {
    baseSkus = baseProduct.skus
  } else if (Array.isArray(baseProduct?.sku)) {
    baseSkus = baseProduct.sku
  }

  if (baseSkus.length) {
    return baseSkus.find(sku => sku?.default || sku?.isDefault) || baseSkus[0]
  }

  return baseProduct
}

function resolveCouponSourceValue(source) {
  if (!source) {
    return ''
  }

  if (typeof source === 'string') {
    return source
  }

  if (Array.isArray(source) && source.length) {
    return resolveCouponSourceValue(source[0])
  }

  if (typeof source === 'object') {
    const directValue =
      source.code ||
      source.couponCode ||
      source.promotionCode ||
      source.offerCode ||
      source.offerId

    if (directValue) {
      return directValue
    }
  }

  return ''
}

function resolveProductCouponCode(productCandidate = {}, fallbackProduct = {}) {
  const sources = [
    productCandidate?.promotion,
    fallbackProduct?.promotion,
    productCandidate?.coupon_code,
    fallbackProduct?.coupon_code,
    productCandidate?.couponCode,
    fallbackProduct?.couponCode,
    productCandidate?.coupon?.code,
    fallbackProduct?.coupon?.code,
  ]

  for (const source of sources) {
    const resolved = resolveCouponSourceValue(source)
    if (resolved) {
      return resolved
    }
  }

  return ''
}

function trackPathToPurchaseSelectItem(options = {}) {
  const {
    product: productCandidate = {},
    baseProduct = {},
    itemListName = '',
    itemIndex = 0,
    sponsorId = '',
    affiliation = '',
    categoryName = '',
    selectedMultiVarSkuPrice = 0,
    selectedVariantLabel = '',
  } = options || {}

  const poAffiliation = getPersonalOfferAffiliationForTracking()

  const resolvedSku = resolveSelectItemSku(productCandidate, baseProduct)
  const baseProps = baseProduct?.properties || {}
  const skuProps = resolvedSku?.skuProperties || resolvedSku?.properties || {}
  const parsedIndex = Number(itemIndex)
  const resolvedIndex = Number.isFinite(parsedIndex) ? parsedIndex : 0
  const listName =
    itemListName ||
    baseProps?.itemListName ||
    categoryName ||
    skuProps?.itemListName ||
    'all products'

  const affiliationCandidates = [
    poAffiliation,
    affiliation,
    sponsorId,
    resolvedSku?.properties?.brandAffiliateId,
    resolvedSku?.properties?.affiliationId,
    resolvedSku?.properties?.affiliation,
    baseProps?.brandAffiliateId,
    baseProps?.affiliationId,
    baseProps?.affiliation,
  ]

  let resolvedAffiliation =
    affiliationCandidates.find(
      candidate => typeof candidate === 'string' && candidate.trim()
    ) ||
    DEFAULT_AFFILIATION ||
    'undefined'

  resolvedAffiliation = `${resolvedAffiliation}`.trim() || 'undefined'

  const rawDiscount =
    resolvedSku?.totalValue?.totaldiscount ??
    resolvedSku?.totalValue?.discount ??
    resolvedSku?.discount ??
    baseProduct?.totalValue?.totaldiscount ??
    baseProduct?.totalValue?.discount ??
    0

  const rawPrice =
    resolvedSku?.totalValue?.priceAfterDiscount ??
    (Array.isArray(resolvedSku?.prices) && resolvedSku.prices[0]
      ? resolvedSku.prices[0]?.price
      : undefined) ??
    resolvedSku?.price ??
    baseProduct?.totalValue?.priceAfterDiscount ??
    (Array.isArray(baseProduct?.prices) && baseProduct.prices[0]
      ? baseProduct.prices[0]?.price
      : undefined) ??
    baseProduct?.price ??
    0

  const quantityValue =
    toQuantity(resolvedSku?.quantity ?? baseProduct?.quantity ?? 1) || 1

  const itemPayload = {
    item_id:
      resolvedSku?.identifier ||
      resolvedSku?.skuId ||
      resolvedSku?.itemId ||
      baseProduct?.identifier ||
      baseProduct?.itemId ||
      '',
    item_name: skuProps?.name || baseProps?.name || baseProps?.slug || '',
    affiliation: resolvedAffiliation,
    coupon_code: 'NA',
    discount: toAmount(rawDiscount),
    index: resolvedIndex,
    item_brand: resolveItemBrand({ skuProps }),
    item_category:
      skuProps?.productCategory ||
      baseProps?.productCategory ||
      baseProps?.categoryId ||
      categoryName ||
      '',
    item_variant:
      selectedVariantLabel || getItemVariant(resolvedSku || baseProduct || {}),
    price: toAmount(selectedMultiVarSkuPrice || rawPrice),
    quantity: quantityValue,
  }

  const personalOfferData = getPersonalOfferTrackingData()

  const payload = {
    event: 'select_item',
    ...personalOfferData,
    ecommerce: {
      item_list_name: listName,
      items: [itemPayload],
    },
  }

  trackDataLayer(payload)

  return payload
}

function getShippingTierLabel(method = {}) {
  if (!method) {
    return ''
  }

  const properties = method?.properties || {}
  const baseLabel =
    method?.displayName ||
    method?.name ||
    properties?.name ||
    properties?.label ||
    method?.id ||
    ''

  const timeline =
    properties?.deliveryWindow ||
    properties?.deliveryTime ||
    method?.deliveryWindow ||
    method?.deliveryTime ||
    ''

  if (baseLabel && timeline) {
    return `${baseLabel} (${timeline})`
  }

  return baseLabel || timeline || ''
}

function trackPathToPurchasePurchaseEvent(options = {}) {
  const {
    cartData = {},
    orderId = '',
    affiliateLink = '',
    distributorId = '',
    offerPitchId = '',
    orderType = '',
    couponCode = '',
    itemAffiliation = '',
  } = options || {}

  const totals = cartData?.value || {}
  const transactionValue = toAmount(
    totals?.grandTotal ?? totals?.priceAfterDiscount ?? totals?.totalAmountPaid
  )

  const discountAmt =
    totals?.totalDiscount ?? totals?.cartDiscount ?? totals?.overAllDiscount
  const orderDiscount = discountAmt ? toAmount(discountAmt) : 'NA'
  const orderTax = toAmount(totals?.overAllTax ?? totals?.tax)
  const shippingCharge = toAmount(
    totals?.cartDeliveryChargeAfterDiscount ?? totals?.cartDeliveryCharge
  )
  const resolvedCoupon = resolveCartCoupon(cartData, couponCode)
  const resolvedOffer =
    getPersonalOfferName() ||
    resolvePromotionDisplayMessages(cartData, resolvedCoupon)
  const defaultAffiliation = resolveDefaultAffiliation(
    cartData,
    itemAffiliation,
    distributorId
  )

  const items = mapCartItemsToPathToPurchase(cartData, {
    resolvedCoupon,
    defaultAffiliation,
  })

  const shippingAddress = cartData?.deliveryDetails?.address || {}
  const personalOfferData = getPersonalOfferTrackingData({
    includeBuyNow: false,
  })
  const buyNowTrackingData = getBuyNowTrackingData()

  const payload = {
    event: 'purchase',
    ...personalOfferData,
    ...buyNowTrackingData,
    user_affiliate_link: affiliateLink || '',
    user_dist_id: distributorId || '',
    offer_pitch_id: offerPitchId || '',
    order_type: orderType || '',
    override_cart: isToShowOverrideSubscription() ? 'yes' : 'no',
    shipping_country: shippingAddress?.country || '',
    shipping_region: shippingAddress?.state || shippingAddress?.region || '',
    ecommerce: {
      transaction_id: orderId || '',
      currency: cartData?.currencyCode || '',
      value: transactionValue,
      offer: resolvedOffer || '',
      discount: orderDiscount,
      tax: orderTax,
      shipping: shippingCharge,
      items,
    },
  }

  trackDataLayer(payload)

  return payload
}

/**
 * Pushes a `purchase` event into the NUi / Sales.AI Insights pixel.
 *
 * Mirrors `trackPathToPurchasePurchaseEvent` but emits through
 * `window.ExperiencePixel.track(...)` instead of the GTM data layer.
 * Safe no-op when the pixel script has not loaded.
 *
 * @param {Object} options
 * @param {Object} [options.cartData]   - Cart response payload
 * @param {string} [options.orderId]    - Order id from checkoutContainer
 */
function trackSalesAIPixelPurchaseEvent(options = {}) {
  try {
    const {
      cartData = {},
      orderId = '',
      transactionId = '',
      paymentMethod = 'credit_card',
      accountId = '',
      personId = '',
    } = options || {}

    const totals = cartData?.value || {}
    const total = toAmount(
      totals?.grandTotal ??
        totals?.priceAfterDiscount ??
        totals?.totalAmountPaid
    )
    const currency = cartData?.currencyCode || ''
    const resolvedCoupon = resolveCartCoupon(cartData, '')
    const discount = toAmount(
      totals?.totalDiscount ??
        totals?.cartDiscount ??
        totals?.overAllDiscount ??
        0
    )
    const tax = toAmount(totals?.overAllTax ?? totals?.tax ?? 0)
    const shipping = toAmount(
      totals?.cartDeliveryChargeAfterDiscount ?? totals?.cartDeliveryCharge ?? 0
    )

    const subtotalFromItems = Array.isArray(cartData?.items)
      ? cartData.items.reduce((sum, item) => {
          const sku = item?.skus?.[0] || {}
          const itemQuantity = Number(item?.quantity) || 0
          const itemPrice = toAmount(
            item?.totalValue?.priceAfterDiscount ??
              sku?.price ??
              item?.unitPrice ??
              item?.price ??
              item?.salePrice
          )
          return sum + itemPrice * itemQuantity
        }, 0)
      : 0
    const subtotal = toAmount(
      totals?.priceAfterDiscount ?? totals?.subTotal ?? subtotalFromItems
    )

    const items = Array.isArray(cartData?.items)
      ? cartData.items.map(item => {
          const sku = item?.skus?.[0] || {}
          const quantityValue = Number(item?.quantity) || 0
          const priceValue = toAmount(
            item?.totalValue?.priceAfterDiscount ??
              sku?.price ??
              item?.unitPrice ??
              item?.price ??
              item?.salePrice
          )
          return {
            productId:
              sku?.skuId ||
              sku?.identifier ||
              item?.skuId ||
              item?.sku ||
              item?.productId ||
              '',
            sku:
              sku?.skuId ||
              sku?.identifier ||
              item?.skuId ||
              item?.sku ||
              item?.productId ||
              '',
            name:
              sku?.skuProperties?.name ||
              sku?.properties?.name ||
              item?.properties?.name ||
              '',
            price: priceValue,
            quantity: quantityValue,
            lineTotal: toAmount(priceValue * quantityValue),
          }
        })
      : []

    const payload = {
      accountId: accountId || '',
      personId: personId || '',
      orderId: orderId || '',
      transactionId: transactionId || orderId || '',
      items,
      currency,
      subtotal,
      tax,
      shipping,
      discount,
      total,
      paymentMethod: paymentMethod || '',
      couponCode: resolvedCoupon || null,
    }

    emitSalesAIPixelEvent('purchase', payload)

    return payload
  } catch (error) {
    console.error('Error in trackSalesAIPixelPurchaseEvent', error)
    return {}
  }
}

/**
 * Tracks signup events.
 *
 * @param {Object} options
 * @param {string} [options.eventName='Signup'] - Name of the event
 * @param {string} options.eventType - Type of the event
 */
function trackSignUpEvents(options) {
  const { eventName = 'Signup', eventType } = options || {}
  const gtmData = {
    event: eventName,
    type: eventType,
  }
  trackDataLayer(gtmData)
}

/*
 *Tracks participants invite Events
 */

function getUserTypeForParticipantIntvite(customerContainer) {
  const userType =
    customerContainer?.profileResponse?.accounts?.accountType || ''
  if (userType == 'Brand Affiliate') {
    return 'ba-i'
  } else if (userType == 'Brand Affiliate - Business Entity') {
    return 'ba-be'
  }
}

function trackParticipantInviteEvent(customerContainer) {
  const userType = getUserTypeForParticipantIntvite(customerContainer)
  const gtmData = {
    event: 'InviteUser',
    account_change: userType,
  }
  trackDataLayer(gtmData)
}

function trackParticipantSendInviteEvent(customerContainer) {
  const userType = getUserTypeForParticipantIntvite(customerContainer)
  const gtmData = {
    event: 'AgreeInvite',
    account_change: userType,
  }
  trackDataLayer(gtmData)
}

function trackOrderConfirmationEvent(options = {}) {
  const { type, transactionId, price, product } = options
  const gtmData = {
    event: type,
    transaction_id: transactionId,
    currency: getCurrency(),
    value: price,
    items: product,
  }
  trackDataLayer(gtmData)
}

async function trackUTTConversion(options) {
  const enableImpactTracking = convertToBoolean(
    APPConfig.getAppConfig()?.enableImpactTracking
  )

  // Tracking will happen only if UTT cookie is present
  // If uttInfo is undefined or an empty object, return.
  const uttInfo = getUTTInfo()
  if (
    !uttInfo ||
    (typeof uttInfo === 'object' && Object.keys(uttInfo)?.length === 0)
  ) {
    return
  }

  const {
    orderId,
    customerId,
    customProfileId,
    customerEmail,
    currencyCode,
    orderPromoCode,
    orderDiscount,
    items,
    customerStatus,
  } = options || {}
  const sha1CustomerEmail = await encodeSHA1(customerEmail)
  if (enableImpactTracking) {
    console.log('trackUTTConversion')
    if (typeof ire != 'undefined') {
      console.log('ire found and tracking', options)
      ire(
        'trackConversion',
        49210,
        {
          orderId: orderId,
          customProfileId: customProfileId,
          customerId: customerId,
          customerEmail: sha1CustomerEmail,
          customerStatus: customerStatus,
          currencyCode: currencyCode,
          orderPromoCode: orderPromoCode,
          orderDiscount: orderDiscount,
          items: items,
        },
        {
          verifySiteDefinitionMatch: true,
        }
      )
    }
  }
}

async function trackImpactIdentify(customerContainer) {
  if (typeof ire != 'undefined') {
    const customerResponse =
      customerContainer?.profileResponse?.customer ||
      customerContainer?.profileResponse
    // when customer call is made for first time, data is found inside customer node
    const customerId = customerResponse?.accounts?.referenceAttribute || ''
    const email = customerResponse?.email || ''
    const encryptedEmail = email ? await encodeSHA1(email) : ''
    const customProfileId = customerResponse?.id || ''
    // 5. Pass customer id as Nu Skinid
    // 6. Pass Equinox id as profile id
    const postData = {
      customerId,
      customerEmail: encryptedEmail,
      customProfileId,
    }
    if (IS_BROWSER) {
      const isLoginCallbackPage = window.location.pathname.includes(
        pageNames.loginCallback
      )
      if (isLoginCallbackPage) {
        if (customerId != '') {
          // if current page is login callback page,
          // track only for register user
          console.log(
            'identify data isLoginCallbackPage - registered user',
            // postData,
            window.location.pathname
          )
          ire('identify', postData)
        } else {
          // don't track for guest user profile call
          // just adding console statement
          console.log(
            'identify data isLoginCallbackPage - guest tracking blocked, will be tracked only for register user',
            // postData,
            window.location.pathname
          )
        }
      } else {
        console.log(
          'identify data (normal page - not login callback page)',
          // postData,
          window.location.pathname
        )
        ire('identify', postData)
      }
    }
  }
}

function trackCheckoutGiftReceipt(options) {
  const { eventName = 'select_gift', eventType, message } = options || {}
  const gtmData = {
    event: eventName,
    eventType: eventType,
    message: message,
  }
  trackDataLayer(gtmData)
}

function trackCheckoutCouponCodeEvents(options) {
  const { eventName = 'checkout_coupon', coupon } = options || {}
  const gtmData = {
    event: eventName,
    coupon: coupon,
  }
  trackDataLayer(gtmData)
}

function trackExpressCheckoutEvents(options) {
  const { eventName = 'express_checkout' } = options || {}
  const gtmData = {
    event: eventName,
    ...options,
  }
  trackDataLayer(gtmData)
}

function trackSaveforLater(gtmData) {
  const payLoad = {
    event: 'save_for_later',
    products: [
      {
        name: gtmData.name || '',
        id: gtmData.skuId || '',
        price: gtmData.totalPrice || '',
        quantity: gtmData.quantity || '',
        category: gtmData.categoryId || '',
      },
    ],
    source: gtmData.source,
  }
  trackDataLayer(payLoad)
}

function trackMovetoCart(gtmData) {
  const payLoad = {
    event: 'move_to_cart',
    products: [
      {
        name: gtmData.name || '',
        id: gtmData.skuId || '',
        price: gtmData.totalPrice || '',
        quantity: gtmData.quantity || '',
        category: gtmData.categoryId || '',
      },
    ],
    source: gtmData.source,
  }
  trackDataLayer(payLoad)
}

function trackCartAddition(options) {
  const { postData = {}, source = '' } = options || {}
  const payLoad = {
    event: 'cart_addition',
    products: [
      {
        name: postData?.[0]?.properties?.slug || '',
        id: postData?.[0]?.skus?.[0]?.skuId || '',
        price: postData?.[0]?.properties?.price || '',
        quantity: postData?.[0]?.quantity || '',
        category: postData?.[0]?.properties.categoryId || '',
      },
    ],
    source: source,
  }
  trackDataLayer(payLoad)
}

function trackCancelOrderEvent(options) {
  const { gtmData = {}, cancel_reason = '' } = options || {}
  const actionfeild = {
    id: gtmData?.orderInfo?.orderDetails?.id || '',
    memberId: gtmData?.customer?.userTracking?.userId || '',
    orderType: gtmData?.otherproperties?.orderType || '',
    revenue:
      gtmData?.orderInfo?.orderFinancials?.orderPriceInfo?.[0]?.totalSale || '',
    tax: gtmData?.orderInfo?.orderFinancials?.orderPriceInfo?.[0]?.tax || '',
    shipping:
      gtmData?.orderInfo?.orderFinancials?.orderPriceInfo?.[0]
        ?.fulfilmentPriceInfo?.fulfilmentSale || '',
    sponsor_id: gtmData?.otherproperties?.sponsorId || '',
    action: 'purchase',
    affiliation: 'Online Store',
    origin: 'Web',
    coupon: '',
  }
  const products =
    gtmData?.orderInfo?.orderDetails?.orderItems?.map(item => ({
      name: item?.item?.itemInfo?.otherProperties?.slug || '',
      id: item?.item?.itemInfo?.id || '',
      price: item?.item?.itemInfo?.sku?.priceInfo?.[0]?.salePrice || '',
      quantity: item?.item?.itemInfo?.quantity || '',
      brand: item?.item?.itemInfo?.sku?.skuInfo?.brand || 'Nuskin',
      category: item?.item?.itemInfo?.otherProperties?.categoryId || '',
      variant: item?.item?.itemInfo?.otherProperties?.variant || '',
    })) || []

  const payLoad = {
    event: 'cancel-order',
    ecommerce: {
      currencyCode: gtmData?.otherproperties?.currencyFormat || '',
      purchase: {
        actionField: { ...actionfeild },
        products: products,
      },
      cancel_reason: cancel_reason,
    },
  }
  trackDataLayer(payLoad)
}

const trackNavigationClick = (navigationType, linkText, linkUrl) => {
  TagManager.dataLayer({
    dataLayer: {
      event: 'navigation_click',
      navigation_type: navigationType,
      link_text: linkText || '',
      link_url: linkUrl || '',
    },
  })
}

function trackPathToPurchaseBeginCheckout(options = {}) {
  const {
    responseData = [],
    sponsorId = '',
    discount = 0,
    item_list_name,
    totalValue,
    cartData = {},
    offer,
  } = options

  if (!enableNewFeature('enableBeginCheckoutGTMEvent')) {
    return null
  }

  const currency = getCurrency()
  const items = new Array(responseData.length)

  const derivedCartData = {
    ...(cartData || {}),
    items: cartData?.items || responseData,
  }
  const resolvedCoupon = resolveCartCoupon(derivedCartData)
  const resolvedOfferCandidate =
    offer ?? resolvePromotionDisplayMessages(derivedCartData, resolvedCoupon)
  const resolvedOffer =
    getPersonalOfferName() ||
    (resolvedOfferCandidate && `${resolvedOfferCandidate}`.trim()) ||
    'NA'

  const poAffiliation = getPersonalOfferAffiliationForTracking()

  for (let idx = 0; idx < responseData.length; idx++) {
    const item = responseData[idx]
    const firstSku = item?.skus?.[0] || {}
    const itemProps = item?.properties || {}
    const skuProps = firstSku?.properties || {}
    const skuSkuProps = firstSku?.skuProperties || {}
    const prevSessionCartSkuIdsArr = getSessionStorage('prevSessionCartSkuIds')
    const subscriptionFrequency = item.isSubscription
      ? getFrequencyLabel(item.subscription)
      : 'NA'

    items[idx] = {
      item_id: firstSku?.skuId || item?.itemId || '',
      item_name: skuSkuProps?.name || skuProps?.name || itemProps?.name || '',
      affiliation: poAffiliation || sponsorId || 'nuskin',
      discount: item.discount || item.totalValue?.discount || 'NA',
      index: idx,
      item_brand: resolveItemBrand({
        skuProps: firstSku?.skuProperties,
        productProps: firstSku?.properties,
      }),
      item_category:
        skuSkuProps?.productCategory ||
        itemProps?.categoryId ||
        skuProps?.categoryId ||
        'NA',
      item_list_name: itemProps?.listName || item_list_name || '',
      coupon_code: 'NA',
      item_variant: getItemVariant(item?.skus?.[0] || {}),
      price: firstSku?.price || item.totalValue?.priceAfterDiscount || 0,
      quantity: item.quantity || 1,
      item_subscription: item.isSubscription || false,
      item_subscription_override: isToShowOverrideSubscription() ? 'yes' : 'no',
      item_subscription_frequency: subscriptionFrequency,
      item_persistent:
        prevSessionCartSkuIdsArr?.includes(firstSku?.skuId) || false,
    }
  }

  const prefix = getTrackingPrefix()
  const personalOfferData = getPersonalOfferTrackingData({
    includeBuyNow: false,
  })
  const buyNowTrackingData = getBuyNowTrackingData()

  const gtmData = {
    event: prefix ? `${prefix}begin_checkout` : 'begin_checkout',
    ...personalOfferData,
    ...buyNowTrackingData,
    ecommerce: {
      currency,
      value: totalValue,
      offer: resolvedOffer,
      discount,
      override_cart: isToShowOverrideSubscription() ? 'yes' : 'no',
      items,
    },
  }

  trackDataLayer(gtmData)
}

function trackRedesignProductViewEvent(data = {}) {
  const { type = '', sponsorId, searchTerm, categoryName, isQuickview } = data

  const personalOfferData = getPersonalOfferTrackingData({
    includeBuyNow: false,
  })
  const poAffiliation = getPersonalOfferAffiliationForTracking()
  const locale = getLocaleCodeFromUrl() || ''
  const countryCode = locale.split('_')?.[1] || ''
  const contextValue = checkMysiteOrPersonalOffer()
  const isPersonalOfferPage = contextValue === 'personal_offer'

  const GTMLayer = {
    event: type,
    ...personalOfferData,
    ...(type !== 'view_item' &&
      isPersonalOfferPage && { country_code: countryCode }),
    ecommerce: {
      ...(type !== 'view_item' && {
        item_list_name: data?.category?.parentId || searchTerm || '',
      }),
      ...(type === 'view_item' && {
        currency: getCurrency(),
        value:
          data?.value ||
          (data?.product?.[0]
            ? (findDefaultSku({ sku: data.product[0]?.sku }) || data.product[0])
                ?.totalValue?.priceAfterDiscount ||
              (findDefaultSku({ sku: data.product[0]?.sku }) || data.product[0])
                ?.prices?.[0]?.price ||
              0
            : 0),
      }),
      items: data?.product?.map((product, i) => {
        const skuOrProduct = findDefaultSku({ sku: product?.sku }) || product
        const skuProps =
          skuOrProduct?.skuProperties || skuOrProduct?.properties || {}
        const itemBrand = resolveItemBrand({
          skuProps: skuOrProduct?.skuProperties,
          productProps: skuOrProduct?.properties,
        })

        let itemView = 'NA'
        if (isQuickview) {
          if (data?.searchTerm || searchTerm) {
            itemView = 'Search quickview'
          } else if (
            data?.category?.parentId ||
            data?.category?.properties?.name ||
            categoryName
          ) {
          } else if (
            data?.category?.parentId ||
            data?.category?.properties?.name ||
            categoryName
          ) {
            itemView = 'Category quickview'
          } else {
            itemView = 'PLP quickview'
          }
        } else if (data?.searchTerm || searchTerm) {
          itemView = 'Search results'
        } else if (data?.product?.length > 1) {
          itemView = 'Product listing'
        } else {
          itemView = 'PDP'
        }

        const baseItem = {
          item_id:
            skuOrProduct?.skuId ||
            skuOrProduct?.identifier ||
            product?.itemId ||
            'NA',
          item_name: skuOrProduct?.properties?.name || 'NA',
          affiliation: poAffiliation || sponsorId || 'nuskin',
          coupon_code: 'NA',
          discount: skuOrProduct?.totalValue?.totaldiscount || 'NA',
          index: i,
          item_brand: itemBrand,
          item_category:
            product?.properties?.productCategory ||
            skuOrProduct?.properties?.productCategory ||
            data?.category?.properties?.name ||
            product?.properties?.categoryId ||
            skuOrProduct?.properties?.categoryId ||
            categoryName ||
            'NA',
          item_variant: getItemVariant(skuOrProduct || product),
          price:
            skuOrProduct?.totalValue?.priceAfterDiscount ??
            skuOrProduct?.prices?.[0]?.price ??
            'NA',
          quantity: 1,
          unit_price: skuOrProduct?.totalValue?.originalPrice ?? 'NA',
          unit_sale_price: skuOrProduct?.totalValue?.priceAfterDiscount ?? 'NA',
          product_image_url: skuOrProduct?.properties?.primaryimage,
        }
        if (type === 'view_item') {
          baseItem.item_subscription =
            convertToBoolean(skuOrProduct?.properties?.issubscription) ||
            convertToBoolean(product?.properties?.isSubscription) ||
            false
          baseItem.item_subscription_frequency =
            getFrequencyLabel(product?.subscription) ||
            (baseItem.item_subscription ? 'monthly' : 'NA')
          baseItem.item_type =
            product?.replacementProduct?.length > 0 ? 'replacement' : 'standard'
          baseItem.item_view = itemView
          baseItem.replacement_product = skuOrProduct?.properties
            ?.discontinuedSkuReplacement
            ? 'available'
            : 'not available'
          baseItem.item_list_name =
            data?.category?.parentId || searchTerm || 'NA'
        }
        return baseItem
      }),
    },
  }
  trackDataLayer(GTMLayer)
}

function normalizeItemVariant(sku = {}) {
  const skuProps = sku?.skuProperties || sku?.properties || {}
  if (skuProps?.variantLabel) {
    return skuProps.variantLabel
  }
  const rawType = `${sku?.type || skuProps?.type || ''}`.toLowerCase()
  if (rawType === 'bundle' || rawType === 'skukit' || rawType === 'kit') {
    return 'bundle-kit'
  }
  return rawType || 'Single'
}

function normalizeOfferString(value) {
  if (value === undefined || value === null) {
    return ''
  }
  return value.toString().replaceAll('_', ' ').trim()
}

function resolvePathToPurchaseOffer(explicitOffer) {
  if (explicitOffer) {
    return normalizeOfferString(explicitOffer)
  }
  const contextValue = checkMysiteOrPersonalOffer()
  return normalizeOfferString(contextValue)
}

function resolvePathToPurchaseAffiliation({
  product = {},
  sponsorId = '',
} = {}) {
  const productProps = product?.properties || {}
  const candidates = [
    product?.affiliation,
    productProps?.brandAffiliateId,
    productProps?.affiliationId,
    productProps?.affiliation,
    sponsorId,
  ]

  for (const candidate of candidates) {
    if (
      (typeof candidate === 'string' && candidate.trim()) ||
      (typeof candidate === 'number' && Number.isFinite(candidate))
    ) {
      return candidate.toString().trim()
    }
  }

  const contextValue = checkMysiteOrPersonalOffer()
  if (typeof contextValue === 'string' && contextValue.trim()) {
    const normalized = contextValue.toLowerCase()
    if (normalized.includes('storefront')) {
      return 'resale'
    }
    if (normalized.includes('personal')) {
      return 'nuskin'
    }
  }

  return 'undefined'
}

function normalizeAddToCartProducts(responseData) {
  if (Array.isArray(responseData)) {
    return responseData.filter(Boolean)
  }
  return responseData ? [responseData] : []
}

function getPrimarySku(product = {}) {
  return product?.skus?.[0] || {}
}

function resolveAddToCartQuantity(product, sku, fallbackQuantity) {
  const candidates = [
    product?.quantity,
    fallbackQuantity,
    sku?.quantity,
    sku?.qty,
  ]
  for (const candidate of candidates) {
    const numeric = Number(candidate)
    if (Number.isFinite(numeric) && numeric > 0) {
      return numeric
    }
  }
  return 1
}

function toGtmNumber(value) {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value
  }

  if (typeof value === 'string') {
    const sanitized = value.replaceAll(/[^0-9.-]+/g, '')
    const parsed = Number.parseFloat(sanitized)
    return Number.isNaN(parsed) ? 0 : parsed
  }

  return 0
}

function resolveAddToCartMonetaryValue(candidates = [], defaultValue = 0) {
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || candidate === '') {
      continue
    }
    const numeric = toGtmNumber(candidate)
    if (!Number.isNaN(numeric)) {
      return Number(numeric.toFixed(2))
    }
  }
  return Number(toGtmNumber(defaultValue).toFixed(2))
}

function resolveAddToCartUnitPrice(product, sku, explicitPrice) {
  return resolveAddToCartMonetaryValue(
    [
      product?.unitValue?.priceAfterDiscount,
      product?.totalValue?.priceAfterDiscount,
      sku?.price,
      explicitPrice,
    ],
    0
  )
}

function resolveAddToCartDiscount(product = {}, sku = {}) {
  return resolveAddToCartMonetaryValue(
    [
      product?.discount,
      product?.totalValue?.discount,
      product?.unitValue?.discount,
      sku?.totalValue?.discount,
      sku?.discount,
    ],
    0
  )
}

function resolveAddToCartItemIndex(product = {}, baseIndex = 0, position = 0) {
  return typeof product?.index === 'number'
    ? product.index
    : baseIndex + position
}

function resolveAddToCartItemType(product = {}, fallback = '') {
  return product?.item_type || fallback || ''
}

function resolveAddToCartItemView(product = {}, fallback = '') {
  return product?.item_view || fallback || ''
}

function resolveItemCouponCode(item = {}, cartResponse = {}) {
  if (item?.coupon_code) {
    return item.coupon_code
  }
  if (item?.couponCode) {
    return item.couponCode
  }

  const promoCodes = cartResponse?.promotionDetails?.codes
  if (Array.isArray(promoCodes) && promoCodes.length > 0) {
    const firstCode = promoCodes.map(codeObj => codeObj?.code).find(Boolean)
    if (firstCode) {
      return firstCode
    }
  }

  return ''
}

function buildAddToCartItemPayload({
  product,
  sku,
  skuProps,
  sponsorId,
  unitPrice,
  quantity,
  discountValue,
  itemIndex,
  subscriptionFrequency,
  itemType,
  itemView,
}) {
  return {
    item_id: product?.itemId || sku?.skuId || '',
    item_name:
      skuProps?.name || product?.properties?.name || skuProps?.slug || '',
    affiliation: resolvePathToPurchaseAffiliation({
      product,
      sponsorId,
    }),
    coupon_code: resolveItemCouponCode(product),
    discount: discountValue,
    index: itemIndex,
    item_brand: resolveItemBrand({
      skuProps,
      productProps: product?.properties,
    }),
    item_category:
      product?.properties?.categoryId || skuProps?.categoryId || '',
    item_variant: normalizeItemVariant(sku),
    price: unitPrice,
    quantity,
    item_subscription: Boolean(product?.isSubscription),
    item_subscription_override: isToShowOverrideSubscription() ? 'yes' : 'no',
    item_subscription_frequency: subscriptionFrequency,
    item_type: itemType,
    item_view: itemView,
  }
}

function buildAddToCartMetadataEntry({
  product,
  sku,
  unitPrice,
  discountValue,
  quantity,
  subscriptionFrequency,
  itemType,
  itemView,
  itemIndex,
}) {
  return {
    product,
    sku,
    unitPrice,
    discount: discountValue,
    quantity,
    subscriptionFrequency,
    item_type: itemType,
    item_view: itemView,
    index: itemIndex,
  }
}

function resolveAddToCartValue(explicitValue, runningTotal) {
  const resolvedValueCandidate = toGtmNumber(explicitValue)
  if (resolvedValueCandidate > 0) {
    return Number(resolvedValueCandidate.toFixed(2))
  }
  return Number(toGtmNumber(runningTotal).toFixed(2))
}

function trackRedesignProductClickEvent(options = {}) {
  const { type, product, sponsorId, categoryId } = options
  const GTMLayer = {
    event: type,
    ecommerce: {
      item_list_name: '',
      items: [
        {
          item_id: product.identifier || '',
          item_name: product.properties?.name || '',
          affiliation: sponsorId || 'nuskin',
          coupon_code: product.promotion || [],
          discount: product.totalValue?.totaldiscount || 'NA',
          index: 0,
          item_brand: resolveItemBrand({ skuProps: product.properties }),
          item_category: categoryId || '',
          item_variant:
            product?.skuProperties?.variantLabel || product?.type || 'Single',
          price:
            product?.totalValue?.priceAfterDiscount ||
            product?.prices?.[0]?.price ||
            '',
          quantity: 1,
        },
      ],
    },
  }
  trackDataLayer(GTMLayer)
}

function trackRedesignAddToCartEvent(options = {}) {
  const {
    type,
    responseData = [],
    sponsorId,
    quantity,
    price,
    item_type = '',
    item_view = '',
    offer = '',
    itemIndex,
  } = options || {}

  let normalizedData = []
  if (Array.isArray(responseData)) {
    normalizedData = responseData
  } else if (responseData) {
    normalizedData = [responseData]
  }

  const personalOfferName = getPersonalOfferName()
  const poAffiliation = getPersonalOfferAffiliationForTracking()

  let resolvedOffer = personalOfferName || offer
  if (!resolvedOffer && normalizedData.length > 0) {
    const firstProduct = normalizedData[0]
    if (
      Array.isArray(firstProduct?.promotion) &&
      firstProduct.promotion.length > 0
    ) {
      resolvedOffer = firstProduct.promotion?.map(val => val.message || '')
      resolvedOffer = [...new Set(resolvedOffer)].toString()
    }
  }

  const items = normalizedData.map((product, idx) => {
    const isReplacementProduct = product?.replacementProduct?.length > 0
    const skuOrProduct =
      product?.skus?.[0] || findDefaultSku({ sku: product?.sku }) || product
    const skuProps =
      skuOrProduct?.skuProperties || skuOrProduct?.properties || {}
    const productProps = product?.properties || {}

    const itemBrand = resolveItemBrand({
      skuProps: skuOrProduct?.skuProperties,
      productProps: skuOrProduct?.properties,
    })

    const itemCategory =
      productProps?.productCategory ||
      skuProps?.productCategory ||
      productProps?.categoryId ||
      skuProps?.categoryId ||
      'NA'

    const productImageUrl =
      skuProps?.primaryimage || productProps?.primaryimage || 'NA'

    // Determine item_type for each item, similar to trackRedesignRemoveFromCartEvent
    let resolvedItemType = item_type
    if (!resolvedItemType && product?.item_type) {
      resolvedItemType = product.item_type
    }

    const resolvedQuantity = toQuantity(quantity || product?.quantity || 1) || 1

    const resolvedPrice =
      price ||
      product?.totalValue?.priceAfterDiscount ||
      skuOrProduct?.totalValue?.priceAfterDiscount ||
      skuOrProduct?.prices?.[0]?.price ||
      skuOrProduct?.price ||
      0

    const unitSalePrice = Number((resolvedPrice / resolvedQuantity).toFixed(2))

    const resolvedUnitPrice =
      product?.totalValue?.originalPrice ??
      skuOrProduct?.totalValue?.originalPrice ??
      'NA'

    const unitPriceNumeric =
      typeof resolvedUnitPrice === 'number'
        ? resolvedUnitPrice
        : resolvedPrice / resolvedQuantity

    const rawDiscount =
      product?.totalValue?.totaldiscount ??
      product?.totalValue?.discount ??
      skuOrProduct?.totalValue?.totaldiscount ??
      skuOrProduct?.totalValue?.discount ??
      product?.discount ??
      skuOrProduct?.discount ??
      NOT_AVAILABLE

    const numericRawDiscount = Number(rawDiscount)
    const calculatedDiscount = Number(
      Math.max(
        0,
        (unitPriceNumeric - unitSalePrice) * resolvedQuantity
      ).toFixed(2)
    )
    const resolvedDiscount =
      Number.isFinite(numericRawDiscount) &&
      !Number.isNaN(numericRawDiscount) &&
      numericRawDiscount > 0
        ? Number((numericRawDiscount * resolvedQuantity).toFixed(2))
        : calculatedDiscount > 0
          ? calculatedDiscount
          : NOT_AVAILABLE

    const baseItem = {
      item_id:
        skuOrProduct?.skuId ||
        skuOrProduct?.identifier ||
        skuOrProduct?.productId ||
        product?.itemId ||
        productProps?.itemId ||
        product?.identifier ||
        product?.productId ||
        NOT_AVAILABLE,
      item_name:
        skuProps?.name ||
        productProps?.name ||
        skuProps?.slug ||
        productProps?.slug ||
        NOT_AVAILABLE,
      affiliation: poAffiliation || sponsorId || 'nuskin',
      coupon_code: NOT_AVAILABLE,
      discount: resolvedDiscount,
      index: itemIndex || idx,
      item_brand: itemBrand,
      item_category: itemCategory,
      item_variant: getItemVariant(skuOrProduct || product),
      price: resolvedPrice,
      quantity: resolvedQuantity,
      item_subscription: false,
      item_subscription_override: isToShowOverrideSubscription() ? 'yes' : 'no',
      item_subscription_frequency: NOT_AVAILABLE,
      item_type: isReplacementProduct ? 'replacement' : 'standard',
      item_view: item_view || NOT_AVAILABLE,
      unit_price: unitPriceNumeric,
      unit_sale_price: unitSalePrice,
      product_image_url: productImageUrl,
    }

    return baseItem
  })

  const totalValue = items.reduce((sum, item) => {
    const itemPrice = typeof item.price === 'number' ? item.price : 0
    const itemQuantity = typeof item.quantity === 'number' ? item.quantity : 1
    return sum + itemPrice * itemQuantity
  }, 0)

  const personalOfferData = getPersonalOfferTrackingData({
    includeBuyNow: false,
  })
  const buyNowAddToCartData = getBuyNowTrackingData(options)
  const GTMLayer = {
    event: type,
    ...personalOfferData,
    ...buyNowAddToCartData,
    ecommerce: {
      currency: getCurrency(),
      value: price || totalValue || 0,
      offer: resolvedOffer || 'NA',
      items,
    },
  }
  trackDataLayer(GTMLayer)
}

function trackSalesAIPixelAddToCartEvent(options = {}) {
  try {
    const {
      responseData = [],
      quantity,
      price,
      cartId = '',
      cartTotalValue,
      cartTotalItems,
      accountId = '',
      personId = '',
    } = options || {}

    const normalizedData = normalizeAddToCartProducts(responseData)

    const items = normalizedData.map(product => {
      const skuOrProduct =
        product?.skus?.[0] || findDefaultSku({ sku: product?.sku }) || product
      const skuProps =
        skuOrProduct?.skuProperties || skuOrProduct?.properties || {}

      const resolvedQuantity =
        toQuantity(quantity || product?.quantity || 1) || 1
      const resolvedPrice =
        price ||
        product?.totalValue?.priceAfterDiscount ||
        skuOrProduct?.totalValue?.priceAfterDiscount ||
        skuOrProduct?.prices?.[0]?.price ||
        skuOrProduct?.price ||
        0

      return {
        productId:
          skuOrProduct?.skuId ||
          skuOrProduct?.identifier ||
          skuOrProduct?.productId ||
          product?.itemId ||
          product?.identifier ||
          product?.productId ||
          '',
        sku:
          skuOrProduct?.skuId ||
          skuOrProduct?.identifier ||
          skuOrProduct?.productId ||
          product?.itemId ||
          product?.identifier ||
          product?.productId ||
          '',
        productName:
          skuProps?.name ||
          product?.properties?.name ||
          skuProps?.slug ||
          product?.properties?.slug ||
          '',
        variant: getItemVariant(skuOrProduct || product) || null,
        price: toAmount(resolvedPrice),
        quantity: resolvedQuantity,
      }
    })

    const fallbackCartTotalItems = items.reduce(
      (sum, item) => sum + (Number(item?.quantity) || 0),
      0
    )
    const resolvedCartTotalValue =
      cartTotalValue === undefined
        ? items.reduce(
            (sum, item) =>
              sum + toAmount(item.price) * (Number(item.quantity) || 0),
            0
          )
        : cartTotalValue

    items.forEach(item => {
      const quantityValue = Number(item?.quantity) || 0
      const unitPrice = toAmount(item?.price)
      const payload = {
        accountId: accountId || '',
        personId: personId || '',
        cartId,
        productId: item?.productId || '',
        sku: item?.sku || '',
        productName: item?.productName || '',
        variant: item?.variant || null,
        price: unitPrice,
        currency: getCurrency() || '',
        quantity: quantityValue,
        lineTotal: toAmount(unitPrice * quantityValue),
        cartTotalValue: toAmount(resolvedCartTotalValue),
        cartTotalItems:
          cartTotalItems === undefined
            ? fallbackCartTotalItems
            : cartTotalItems,
      }

      emitSalesAIPixelEvent('add_to_cart', payload)
    })
  } catch (error) {
    console.error('Error in trackSalesAIPixelAddToCartEvent', error)
  }
}

function resolveViewCartItemBrand(product = {}, sku = {}) {
  const skuBrand = resolveItemBrand({
    skuProps: sku?.skuProperties,
    fallback: null,
  })
  if (skuBrand) return skuBrand

  const skuPropertiesBrand = resolveItemBrand({
    skuProps: sku?.properties,
    fallback: null,
  })
  if (skuPropertiesBrand) return skuPropertiesBrand

  const nestedProductBrand = resolveItemBrand({
    skuProps: sku?.product?.properties,
    fallback: null,
  })
  if (nestedProductBrand) return nestedProductBrand

  const bundleBrand =
    product?.bundleMandatoryProducts?.[0]?.sku?.[0]?.skuProperties?.brand ||
    product?.bundleMandatoryProducts?.[0]?.sku?.[0]?.properties?.brand

  if (bundleBrand) return bundleBrand

  const productBrand = resolveItemBrand({
    skuProps: product?.properties,
    fallback: null,
  })
  if (productBrand) return productBrand
  return DEFAULT_BRAND
}

function trackRedesignViewCartEvent(options = {}) {
  const {
    type,
    responseData = [],
    price,
    sponsorId,
    coupon_code,
    discount,
    item_list_name,
    item_persistent,
  } = options

  const prevSessionCartSkuIdsArr = getSessionStorage('prevSessionCartSkuIds')
  const poAffiliation = getPersonalOfferAffiliationForTracking()
  const items = []
  responseData.forEach((product, productIndex) => {
    const skus = product?.skus || []
    if (skus.length > 0) {
      // For each SKU in the product, add it as a separate item
      skus.forEach((sku, skuIndex) => {
        items.push({
          item_id: sku?.skuId || product?.itemId,
          item_name:
            sku?.skuProperties?.name ||
            sku?.properties?.name ||
            product?.properties?.name ||
            '',
          affiliation: poAffiliation || sponsorId || 'nuskin',
          coupon_code: 'NA',
          discount: discount || product?.totalValue?.discount || 'NA',
          index: productIndex,
          item_brand: resolveViewCartItemBrand(product, sku),
          item_category:
            product?.properties?.productCategory ||
            sku?.product?.properties?.productCategory ||
            sku?.skuProperties?.productCategory ||
            product?.properties?.categoryId ||
            sku?.properties?.categoryId ||
            'NA',
          item_list_name:
            item_list_name || product?.properties?.listName || 'NA',
          item_variant: getItemVariant(sku || product),
          price:
            product?.totalValue?.priceAfterDiscount ||
            product?.unitValue?.priceAfterDiscount ||
            sku?.price ||
            0,
          quantity: sku?.quantity || product?.quantity || 1,
          item_subscription: product?.isSubscription || false,
          item_subscription_override: isToShowOverrideSubscription()
            ? 'yes'
            : 'no',
          item_subscription_frequency: product?.isSubscription
            ? (product?.subscription?.frequencyPeriod?.toString() || '') +
              (product?.subscription?.frequencyType || '')
            : 'NA',
          item_persistent:
            prevSessionCartSkuIdsArr?.includes(sku?.skuId) ||
            item_persistent ||
            false,
          unit_price:
            product?.totalValue?.originalPrice ??
            sku?.totalValue?.originalPrice ??
            sku?.price ??
            'NA',
          unit_sale_price:
            product?.totalValue?.priceAfterDiscount ??
            product?.unitValue?.priceAfterDiscount ??
            sku?.price ??
            sku?.salePrice ??
            'NA',
          product_image_url:
            sku?.skuProperties?.primaryimage ||
            sku?.properties?.primaryimage ||
            '',
        })
      })
    } else {
      // If no SKUs, add the product itself
      items.push({
        item_id: product?.skuId || product?.itemId || 'NA',
        item_name: product?.properties?.name || 'NA',
        affiliation: poAffiliation || sponsorId || 'nuskin',
        coupon_code:
          resolveProductCouponCode(null, product) || coupon_code || 'NA',
        discount: discount || product?.totalValue?.discount || 'NA',
        index: productIndex,
        item_brand: resolveItemBrand({ skuProps: product?.properties }),
        item_category:
          product?.properties?.productCategory ||
          product?.properties?.categoryId ||
          'NA',
        item_list_name: item_list_name || product?.properties?.listName || 'NA',
        item_variant: getItemVariant(product),
        price:
          product?.totalValue?.priceAfterDiscount ||
          product?.unitValue?.priceAfterDiscount ||
          price ||
          0,
        quantity: product?.quantity || 1,
        item_subscription: product?.isSubscription || false,
        item_subscription_override: isToShowOverrideSubscription()
          ? 'yes'
          : 'no',
        item_subscription_frequency: product?.isSubscription
          ? (product?.subscription?.frequencyPeriod?.toString() || '') +
            (product?.subscription?.frequencyType || '')
          : 'NA',
        item_persistent:
          prevSessionCartSkuIdsArr?.includes(
            product?.skuId || product?.itemId
          ) ||
          item_persistent ||
          false,
        unit_price:
          product?.totalValue?.originalPrice ?? product?.totalPrice ?? 'NA',
        unit_sale_price:
          product?.totalValue?.priceAfterDiscount ??
          product?.unitValue?.priceAfterDiscount ??
          product?.totalPrice ??
          'NA',
        product_image_url: product?.properties?.primaryimage || '',
      })
    }
  })

  const personalOfferData = getPersonalOfferTrackingData()

  const GTMLayer = {
    event: type,
    ...personalOfferData,
    ecommerce: {
      currency: getCurrency(),
      value: price || 0,
      override_cart: isToShowOverrideSubscription() ? 'yes' : 'no',
      items: items,
    },
  }

  trackDataLayer(GTMLayer)
}

function trackRedesignRemoveFromCartEvent(options = {}) {
  const {
    type,
    responseData,
    price,
    quantity,
    sponsorId,
    item_type,
    item_view,
    index,
    coupon_code,
    discount,
    item_list_name,
    item_persistent,
  } = options

  const product = responseData[0]
  const skus = product?.skus || []
  const productProps = product?.properties || {}
  const personalOfferData = getPersonalOfferTrackingData()

  const GTMLayer = {
    event: type,
    ...personalOfferData,
    ecommerce: {
      currency: getCurrency(),
      value: price * quantity || skus[0]?.price * quantity || 0,
      items: skus.map((sku, i) => {
        const skuProps = sku?.skuProperties || sku?.properties || {}
        const subscriptionFrequency = getFrequencyLabel(product?.subscription)
        const isReplacementProduct = product?.replacementProduct?.length > 0

        const unitPrice =
          product?.totalValue?.originalPrice ??
          sku?.totalValue?.originalPrice ??
          sku?.price ??
          'NA'

        const unitSalePrice =
          product?.totalValue?.priceAfterDiscount ??
          sku?.totalValue?.priceAfterDiscount ??
          sku?.prices?.[0]?.price ??
          'NA'

        return {
          item_id: sku?.skuId || product?.itemId,
          item_name: skuProps?.name || 'NA',
          affiliation: sponsorId || 'nuskin',
          coupon_code: 'NA',
          discount:
            discount ||
            product?.totalValue?.discount ||
            product?.totalValue?.totaldiscount ||
            sku?.totalValue?.discount ||
            sku?.totalValue?.totaldiscount ||
            'NA',
          index: index || i,
          item_brand: resolveItemBrand({ skuProps }),
          item_category:
            productProps?.productCategory ||
            skuProps?.productCategory ||
            productProps?.categoryId ||
            skuProps?.categoryId ||
            'NA',
          item_list_name:
            product?.properties?.listName || item_list_name || 'individual',
          item_variant: getItemVariant(sku || product),
          price:
            product?.totalValue?.priceAfterDiscount ||
            product?.unitValue?.priceAfterDiscount ||
            sku?.price ||
            price ||
            0,
          quantity: quantity || product?.quantity || 1,
          item_subscription: product?.isSubscription || false,
          item_subscription_override: isToShowOverrideSubscription()
            ? 'yes'
            : 'no',
          item_subscription_frequency: subscriptionFrequency || 'NA',
          item_persistent: item_persistent || false,
          item_type: isReplacementProduct ? 'replacement' : 'standard',
          item_view: item_view || 'viewcart',
          unit_price: unitPrice,
          unit_sale_price: unitSalePrice,
          product_image_url: skuProps?.primaryimage || '',
        }
      }),
    },
  }
  trackDataLayer(GTMLayer)
}

function trackRedesignViewPromotion(options = {}) {
  const { promotions = [] } = options
  const gtmData = {
    event: 'view_promotion',
    promotions: promotions,
  }

  trackDataLayer(gtmData)
}

function trackRedesignSelectPromotion(options = {}) {
  const { creative_name, creative_slot, promotion_id, promotion_name } =
    options || {}

  const gtmData = {
    event: 'select_promotion',
    creative_name: creative_name,
    creative_slot: creative_slot,
    promotion_id: promotion_id,
    promotion_name: promotion_name,
  }

  trackDataLayer(gtmData)
}

function trackRedesignAddCoupon(options = {}) {
  const { offer, method } = options || {}
  const gtmData = {
    event: 'add_coupon',
    offer: offer,
    method: method,
  }
  trackDataLayer(gtmData)
}

function getFssaiExpiryDuration(daysDifference) {
  if (daysDifference < 0) return 'expired'
  if (daysDifference <= 30) return 'less than 30 days'
  if (daysDifference <= 60) return '30 to 60 days'
  return 'less than 90 days'
}

function getFssaiLicenceTypeLabel(licenseType) {
  if (licenseType === 'FSSAI') return 'fssai'
  if (licenseType === 'Ack-FSSAI') return 'acknowledgement'
  return 'none'
}

export {
  CHECKOUT_EVENT_TYPES,
  TRACKING_EVENTS,
  addUserIdForTracking,
  trackCookie,
  trackEnhancedCart,
  trackPromotionImpression,
  trackProductClick,
  trackEnhancedCheckout,
  trackEnhancedProductImpression,
  trackEnhancedProductDetailImpression,
  trackSearchResult,
  trackRecentSearch,
  trackPredictiveSearch,
  trackMyProfileEvents,
  trackPDPAddToCart,
  trackQuickViewAddToCart,
  trackAddToSubscription,
  trackGTMEvents,
  trackContactMe,
  trackPincodeError,
  trackGSTINError,
  trackAccountUpgrade,
  trackSearchSuggestions,
  getCheckoutProduct,
  getCheckoutProductV2,
  trackSubscriptionOrderNowGTMData,
  getSubscriptionDrawerData,
  getProductsForAgreementSection,
  trackFilter,
  trackCustomCheckoutEvent,
  trackAddShippingInfoEvent,
  trackAddPaymentInfoEvent,
  trackSignUpEvents,
  trackV2SubscriptionButtonClick,
  trackPredictiveSearchEvent,
  trackRecentSearchEvent,
  trackProductClickEvent,
  trackEnhancedCartEvent,
  trackEnhancedCheckoutEvent,
  trackSearchResultEvent,
  trackEnhancedProductViewEvent,
  trackOrderConfirmationEvent,
  trackBuyItAgainEvent,
  trackUTTConversion,
  trackImpactIdentify,
  trackCheckoutCouponCodeEvents,
  trackCheckoutGiftReceipt,
  trackExpressCheckoutEvents,
  trackExpressCheckoutSubmitOrder,
  trackNavigationClick,
  trackPathToPurchaseBeginCheckout,
  trackPathToPurchaseSelectItem,
  trackSaveforLater,
  trackMovetoCart,
  trackCancelOrderEvent,
  trackCartAddition,
  getUserTypeForParticipantIntvite,
  trackParticipantInviteEvent,
  trackParticipantSendInviteEvent,
  trackRedesignProductViewEvent,
  trackRedesignProductClickEvent,
  trackRedesignAddToCartEvent,
  trackSalesAIPixelAddToCartEvent,
  trackRedesignRemoveFromCartEvent,
  trackRedesignViewCartEvent,
  trackPathToPurchasePurchaseEvent,
  trackSalesAIPixelPurchaseEvent,
  trackSalesAIPixelLogin,
  trackSalesAIPixelBeginCheckout,
  trackRedesignViewPromotion,
  trackRedesignSelectPromotion,
  trackRedesignAddCoupon,
  trackMysiteAddCartEvent,
  trackMysiteViewAllEvent,
  getFssaiExpiryDuration,
  getFssaiLicenceTypeLabel,
  getPersonalOfferTrackingData,
  getPersonalOfferAffiliationForTracking,
  getPersonalOfferName,
  sanitizeForGTM,
}
